import ImageCropperModal from './ImageCropperModal';
import React, { useEffect, useState, useRef } from 'react';
import './UserProfileModal.css';
import SettingsShell from './SettingsShell';
import useDeviceLocationCheck, { deviceLocationMessages } from './activities/useDeviceLocationCheck';
import { useModal } from '../services/ModalService';
import AvatarSelectionModal from './AvatarSelectionModal';
import { useFeature } from '../contexts/SubscriptionContext';
import { setLocationTrackingEnabled } from '../contexts/GeolocationServiceContext';
import HealthConnectService from '../services/HealthConnectService';
import DiscordPresenceService from '../services/DiscordPresenceService';
import NativePushNotificationService from '../services/NativePushNotificationService';
import AiModelSettings from './AiModelSettings';
import CustomMcpSettings from './CustomMcpSettings';
import AiCoachSettings, { ExternalCoachAgentSettings } from './AiCoachSettings';
import ExternalCoachDataGrant from './ExternalCoachDataGrant';
import DojoSettingsModal from './DojoSettingsModal';
import ModalLoading from './ModalLoading';
import ProgressPortrait from './ProgressPortrait';
import ProgressImagePanel from './ProgressImagePanel';
import CameraSettingsSection from './camera/CameraSettingsSection';
import BackgroundPositionSettings from './BackgroundPositionSettings';

const DEFAULT_PRIVACY_SETTINGS = {
  workout: ['friends'],
  meal: ['friends'],
  media: ['friends'],
  metric: ['friends'],
  survey: [],
  progress_image: [],
  position: [] // Exact GPS coordinates are private by default
};

const UserProfileModal = ({ close, backendService, scrollToSection, mode = 'profile', onCoachChange, onOpenCompliance, progressPercent, monthPercent = null, yearPercent = null, starCount = null, progressState = null, progressUnavailable = false, onLogout }) => { onCoachChange = onCoachChange || (() => {});
  const AI_EMOJI_OPTIONS = ['🤖', '🧠', '💪', '🏋️', '🥗', '🎯', '⚡', '🔥', '🌟', '👨‍🔬', '👩‍🔬', '🦾', '🐉', '🦁', '🐺'];
  const AI_INTENSITY_LEVELS = [
    { id: 'gentle', label: 'Gentle', description: 'Light reminders, very patient' },
    { id: 'balanced', label: 'Balanced', description: 'Regular check-ins, moderate push' },
    { id: 'intense', label: 'Intense', description: 'Frequent nudges, accountability focus' },
    { id: 'relentless', label: 'Relentless', description: 'Maximum pressure, no mercy' },
    { id: 'custom', label: 'Custom', description: 'Define your own intensity level' }
  ];
  const isPreferencesMode = mode === 'preferences';
  const [targetSection, setTargetSection] = useState(scrollToSection === 'ai-coach' ? 'coach' : scrollToSection);
  const [requirementsKnown, setRequirementsKnown] = useState(false);
  const authenticatedIdentity = backendService.authenticatedIdentity;
  const modalService = useModal();
  const { open } = modalService;
  const savingPreferences = useRef(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [saveLedger, setSaveLedger] = useState([]);
  const ledgerRef = useRef([]);
  const [saveMessage, setSaveMessage] = useState('');
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  const [scopeInvalid, setScopeInvalid] = useState(false);
  const scopeInvalidRef = useRef(false);
  const [broadBaseline, setBroadBaseline] = useState(null);
  useEffect(() => {
    scopeInvalidRef.current = false;
    setScopeInvalid(false); setBroadBaseline(null); ledgerRef.current = []; setSaveLedger([]); setSaveMessage(''); setRefreshNeeded(false);
    const invalidate = () => { scopeInvalidRef.current = true; setScopeInvalid(true); setSaveMessage('Your account scope changed. Drafts are quarantined; close and reopen under the current account.'); };
    return backendService.onAccountScopeChanged?.(invalidate);
  }, [backendService]);
  const notificationsSectionRef = useRef(null);
  const aiCoachSectionRef = useRef(null);
  const [logoutState, setLogoutState] = useState({ pending: false, error: '' });
  const { hasAccess: hasAiCoachFeature, openUpgrade } = useFeature('ai_coach_settings');
  const [isLoading, setIsLoading] = useState(true);
  const [profileOwner, setProfileOwner] = useState(null);
  const [email, setEmail] = useState('');
  const [display_name, setDisplayName] = useState('');
  const [date_of_birth, setDateOfBirth] = useState('');
  const [height, setHeight] = useState('');
  const [heightUnit, setHeightUnit] = useState('cm'); // 'cm' or 'ft'
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [sex, setSex] = useState(''); // 'Male', 'Female', or ''
  const [dietaryRestrictions, setDietaryRestrictions] = useState('');
  const [medicalIssues, setMedicalIssues] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [clerkAvatarUrl, setClerkAvatarUrl] = useState(null); // Avatar from sign-in provider (Google, Facebook, etc.)
  const [avatarFile, setAvatarFile] = useState(null);
  const avatarFileRef = useRef(null);
  const avatarSelectionScope = useRef(null);
  useEffect(() => {
    const scope = { active: true };
    avatarSelectionScope.current = scope;
    avatarFileRef.current = null;
    setAvatarFile(null);
    setAvatarUrl(null);
    return () => { scope.active = false; };
  }, [backendService, authenticatedIdentity]);
  useEffect(() => () => {
    if (avatarUrl?.startsWith('blob:')) URL.revokeObjectURL(avatarUrl);
  }, [avatarUrl]);
  const supportedTimezones = Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone') : [Intl.DateTimeFormat().resolvedOptions().timeZone];

  // AI Coach Settings
  const [aiCoachName, setAiCoachName] = useState('');
  const [aiCoachEmoji, setAiCoachEmoji] = useState('🤖');
  const [aiCoachImageUrl, setAiCoachImageUrl] = useState(null);
  const [aiCoachImageFile, setAiCoachImageFile] = useState(null);
  const aiCoachImageRef = useRef(null);

  const [aiCoachIntensity, setAiCoachIntensity] = useState('balanced');
  const [aiCoachIntensityCustom, setAiCoachIntensityCustom] = useState('');
  const [aiCoachPersonalityPreset, setAiCoachPersonalityPreset] = useState('kata_default');
  const [aiCoachPersonalityCustom, setAiCoachPersonalityCustom] = useState('');
  const [coachAutoAcceptance, setCoachAutoAcceptance] = useState({
    daily_insight_changes: false,
    exercise_coach_changes: false
  });
  const [dojoSettings, setDojoSettings] = useState(null);
  const [savedCoachAutoAcceptance, setSavedCoachAutoAcceptance] = useState({ daily_insight_changes: false, exercise_coach_changes: false });
  const [dojoRequiredSettings, setDojoRequiredSettings] = useState(null);
  const [coachOwnership, setCoachOwnership] = useState(null);
  const coachOwnerType = !scopeInvalid && coachOwnership?.service === backendService && coachOwnership.identity === authenticatedIdentity ? coachOwnership.type : null;
  const [llmDraft, setLlmDraft] = useState({ preset: 'balanced', overrides: {}, providers: [] });
  const captureLlmDraft = React.useCallback((settings) => setLlmDraft(settings || { preset: 'balanced', overrides: {}, providers: [] }), []);

  // Email Verification & Notifications
  const [emailVerified, setEmailVerified] = useState(true);
  const [verificationCode, setVerificationCode] = useState('');
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [notificationPrefs, setNotificationPrefs] = useState({
    // Each notification type has email, sms, and push options
    messages: { email: true, sms: false, push: true },
    dojo_messages: { email: false, sms: false, push: true },
    dojo_activity_completions: { email: false, sms: false, push: true },
    coach_messages: { email: false, sms: false, push: true },
    friend_requests: { email: true, sms: false, push: true },
    dojo_invites: { email: true, sms: false, push: true },
    dojo_join_requests: { email: true, sms: false, push: true },
    activity_comments: { email: true, sms: false, push: true },
    activity_kudos: { email: true, sms: false, push: true },
    scheduled_activities: { email: false, sms: false, push: true },
    weekly_summary: { email: false, sms: false, push: false }
  });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [nativePush] = useState(() => NativePushNotificationService.isNativeAndroid());
  const [nativeNotificationPermission, setNativeNotificationPermission] = useState('prompt');
  const notificationPermission = nativePush ? nativeNotificationPermission : browserNotificationPermission;

  // Location Tracking (disabled by default)
  const [locationTracking, setLocationTracking] = useState(false);
  const [locationCaptureEnabled, setLocationCaptureEnabled] = useState(false);
  const captureDevice = useDeviceLocationCheck(backendService);

  // Health Connect is configured on Android but its last known state is shown everywhere.
  const [hcNative] = useState(() => HealthConnectService.isNative());
  const [hcAvailable, setHcAvailable] = useState(false);
  const [hcStatus, setHcStatus] = useState(null);          // 'Available' | 'NotInstalled' | 'NotSupported' | null
  const [hcPermissionGranted, setHcPermissionGranted] = useState(false);
  const [hcGrantedTypes, setHcGrantedTypes] = useState([]);
  const [hcRawGranted, setHcRawGranted] = useState([]);
  const [hcLastSyncAt, setHcLastSyncAt] = useState(null);
  const [hcReportedAt, setHcReportedAt] = useState(null);
  const [hcSyncing, setHcSyncing] = useState(false);
  const [hcMessage, setHcMessage] = useState('');

  // Only native builds can publish Rich Presence, but linking and preferences work everywhere.
  const [discordSupported] = useState(() => DiscordPresenceService.isSupported());
  const [discordConnected, setDiscordConnected] = useState(false);
  const [discordUsername, setDiscordUsername] = useState(null);
  const [discordBusy, setDiscordBusy] = useState(false);
  const [discordSettings, setDiscordSettings] = useState(DiscordPresenceService.DEFAULT_PRESENCE_SETTINGS);
  const [discordMessage, setDiscordMessage] = useState('');

  // Privacy Settings - now uses arrays of audiences
  const [privacySettings, setPrivacySettings] = useState(DEFAULT_PRIVACY_SETTINGS);
  const [privacyDirtyRows, setPrivacyDirtyRows] = useState({});
  const [progressPrivacyLoaded, setProgressPrivacyLoaded] = useState(false);
  const [progressPortraitPublic, setProgressPortraitPublic] = useState(false);



  /**
   * Privacy audiences that can be selected.
   * Users can select multiple audiences.
   * 'public' overrides all others (everyone can see).
   * 'private' means no one can see (clears all others).
   */
  const PRIVACY_AUDIENCES = [
    { value: 'private', label: '🔒 Private', description: 'Only you can see' },
    { value: 'public', label: '🌍 Public', description: 'Anyone can see' },
    { value: 'friends', label: '👥 Friends', description: 'Your friends' },
    { value: 'dojo', label: '🥋 Dojo', description: 'Your dojo members' },
    { value: 'dojo_chief', label: '👑 Dojo Chief', description: 'Only your dojo chief' }
  ];

  const ACTIVITY_TYPES = [
    { key: 'workout', label: '🏋️ Workouts', description: 'Exercise sessions and training' },
    { key: 'meal', label: '🍽️ Meals', description: 'Food logs and nutrition' },
    { key: 'media', label: '📸 Media', description: 'Activity photos and videos' },
    { key: 'progress_image', label: 'Personal progress image', description: 'Your separate progress picture; sharing is independent of image editing and activity media' },
    { key: 'metric', label: '📊 Metrics', description: 'Body measurements and stats' },

    { key: 'survey', label: '📝 Surveys', description: 'Check-ins and questionnaires' },
    { key: 'position', label: '📍 Position', description: 'Exact GPS coordinates captured when filing an activity (not your saved gym)' }
  ];

  useEffect(() => {
    if (scopeInvalidRef.current) return undefined;
    console.log('[UserProfileModal] Mounted');
    let cancelled = false;
    setIsLoading(true);
    setProfileOwner(null);
    setPrivacyDirtyRows({});
    setProgressPrivacyLoaded(false);
    setPrivacySettings(DEFAULT_PRIVACY_SETTINGS);
    setDojoSettings(null);
    setDojoRequiredSettings(null);
    setRequirementsKnown(false);
    setCoachOwnership(null);
    const fetchUserData = async () => {
      try {
        const userData = await backendService.fetchUserProfile();
        if (cancelled) return;
        console.log('[UserProfileModal] fetchUserData result:', userData);
        setProfileOwner({ service: backendService, identity: authenticatedIdentity, id: userData._id });
        setEmail(userData.email);
        setDisplayName(userData.display_name);
        setDateOfBirth(formatDate(userData.date_of_birth));
        const cm = userData.height;
        setHeight(cm || '');
        if (cm) {
          // Initialize ft/in if we have data
          const inches = cm / 2.54;
          const ft = Math.floor(inches / 12);
          const remIn = Math.round(inches % 12);
          setHeightFt(ft.toString());
          setHeightIn(remIn.toString());
        }
        if (userData.timezone) setTimezone(userData.timezone);
        if (userData.sex) setSex(userData.sex);
        if (userData.dietary_restrictions) setDietaryRestrictions(userData.dietary_restrictions);
        if (userData.medical_issues) setMedicalIssues(userData.medical_issues);
        setAvatarUrl(userData.avatar_url || null);
        setClerkAvatarUrl(userData.clerk_avatar_url || null);
        setProgressPortraitPublic(userData.progress_portrait_public === true);
        try {
          const dojo = await backendService.getMyDojo();
          if (cancelled) return;
          setDojoSettings(dojo ? { id: dojo._id, name: dojo.name, role: dojo.user_membership?.role, coach: dojo.ai_coach_settings || {}, models: dojo.llm_settings || {} } : null);
          if (dojo) {
            try {
              const rule = await backendService.getDojoRequirements(dojo._id);
              if (!cancelled) { setDojoRequiredSettings(rule?.requirements || null); setRequirementsKnown(Boolean(rule?.requirements)); }
            } catch (error) {
              if (!cancelled) setDojoRequiredSettings(null);
            }
          }
          setCoachOwnership({ service: backendService, identity: authenticatedIdentity, type: dojo ? 'dojo' : 'personal' });
        } catch (error) {
          if (cancelled) return;
          setCoachOwnership(null);
          setDojoSettings(null);
        }

        // Load AI Coach settings
        if (userData.ai_coach_settings) {
          const ais = userData.ai_coach_settings;
          if (ais.name !== undefined) setAiCoachName(ais.name);
          if (ais.emoji) setAiCoachEmoji(ais.emoji);
          if (ais.image_url) setAiCoachImageUrl(ais.image_url);

          if (ais.intensity) setAiCoachIntensity(ais.intensity);
          if (ais.intensity_custom) setAiCoachIntensityCustom(ais.intensity_custom);
          if (ais.coach_personality?.preset) setAiCoachPersonalityPreset(ais.coach_personality.preset);
          else if (ais.personality_preset) setAiCoachPersonalityPreset(ais.personality_preset);
          if (ais.coach_personality?.custom) setAiCoachPersonalityCustom(ais.coach_personality.custom);
          else if (ais.personality_custom) setAiCoachPersonalityCustom(ais.personality_custom);
          if (ais.auto_acceptance) {
            setCoachAutoAcceptance(prev => ({ ...prev, ...ais.auto_acceptance }));
            setSavedCoachAutoAcceptance(prev => ({ ...prev, ...ais.auto_acceptance }));
          }
        }

        // Email verification status
        setEmailVerified(userData.email_verified === true);

        // Phone number
        if (userData.phone_number) setPhoneNumber(userData.phone_number);

        // Location tracking
        setLocationTracking(userData.location_tracking === true);
        setLocationCaptureEnabled(userData.location_capture_enabled === true);

        // Notification preferences (handle both old and new format)
        if (userData.notification_preferences) {
          const prefs = userData.notification_preferences;
          // Check if it's new format (has nested objects)
          if (prefs.messages && typeof prefs.messages === 'object') {
            setNotificationPrefs(prev => ({ ...prev, ...prefs }));
          } else {
            // Convert old format to new format
            setNotificationPrefs(prev => ({
              ...prev,
              messages: { email: prefs.email_messages ?? true, sms: false, push: true },
              friend_requests: { email: prefs.email_friend_requests ?? true, sms: false, push: true },
              dojo_invites: { email: prefs.email_dojo_invites ?? true, sms: false, push: true },
              dojo_join_requests: { email: prefs.email_dojo_join_requests ?? true, sms: false, push: true },
              weekly_summary: { email: prefs.email_weekly_summary ?? false, sms: false, push: false }
            }));
          }
        }

        // Load privacy settings
        try {
          const privacyData = await backendService.getPrivacySettings();
          if (cancelled) return;
          setPrivacySettings(prev => ({ ...prev, ...privacyData, progress_image: Array.isArray(privacyData?.progress_image) ? privacyData.progress_image : [] }));
          setPrivacyDirtyRows({});
          setProgressPrivacyLoaded(true);
        } catch (e) {
          if (cancelled) return;
          console.warn('Could not load privacy settings:', e);
        }

        // Load Discord presence settings
        try {
          const discordData = await backendService.getDiscordPresenceSettings();
          if (cancelled) return;
          if (discordData) setDiscordSettings(prev => ({ ...prev, ...discordData }));
        } catch (e) {
          console.warn('Could not load Discord presence settings:', e);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchUserData();
    return () => {
      cancelled = true;
      console.log('[UserProfileModal] Unmounted');
    };
  }, [backendService, authenticatedIdentity]);

  useEffect(() => {
    if (!nativePush) return;
    NativePushNotificationService.getPermission().then(setNativeNotificationPermission).catch(() => setNativeNotificationPermission('denied'));
  }, [nativePush]);

  useEffect(() => {
    // keep a ref in sync so we don't lose the selected file across renders
    avatarFileRef.current = avatarFile;
    console.log('[avatar] avatarFile state changed', {
      hasFile: !!avatarFile,
      name: avatarFile?.name,
      size: avatarFile?.size,
      type: avatarFile?.type,
      avatarUrl: avatarUrl
    });
  }, [avatarFile, avatarUrl]);

  useEffect(() => {
    aiCoachImageRef.current = aiCoachImageFile;
  }, [aiCoachImageFile]);

  // Scroll to the specified section after data loads
  useEffect(() => {
    const target = scrollToSection === 'notifications'
      ? notificationsSectionRef.current
      : (scrollToSection === 'ai-coach' ? aiCoachSectionRef.current : null);
    if (target) {
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [scrollToSection]);

  // Probe Health Connect availability + permissions (Capacitor wrapper only)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hcNative) {
        const saved = await backendService.getHealthConnectStatus().catch((error) => {
          console.warn('Could not load Health Connect status:', error);
          return null;
        });
        if (cancelled || !saved) return;
        setHcAvailable(saved.available);
        setHcStatus(saved.status);
        const activeGrants = HealthConnectService.filterGrantedTypes(saved.granted_types);
        setHcGrantedTypes(activeGrants);
        setHcPermissionGranted(activeGrants.length === HealthConnectService.READ_TYPES.length);
        setHcLastSyncAt(saved.last_sync_at || null);
        setHcReportedAt(saved.updated_at || null);
        return;
      }
      const av = await HealthConnectService.isAvailable();
      if (cancelled) return;
      setHcAvailable(!!av.available);
      setHcStatus(av.status || av.reason || null);
      if (av.available) {
        const status = await HealthConnectService.getPermissionStatus();
        if (cancelled) return;
        setHcGrantedTypes(status.grantedTypes);
        setHcRawGranted(status.rawGranted);
        setHcPermissionGranted(status.hasAll);
      }
      const last = await HealthConnectService.getLastSyncAt();
      if (cancelled) return;
      setHcLastSyncAt(last);
      backendService.updateHealthConnectStatus({
        available: !!av.available,
        status: av.status || av.reason || null,
        granted_types: av.available ? (await HealthConnectService.getPermissionStatus()).grantedTypes : [],
        last_sync_at: last
      }).catch((error) => console.warn('Could not save Health Connect status:', error));
    })();
    return () => { cancelled = true; };
  }, [backendService, hcNative]);

  // The link belongs to the account, not the device, so load it everywhere.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const linked = await backendService.getDiscordLinkStatus().catch((error) => {
        console.warn('Could not load Discord link status:', error);
        return { linked: false, username: null };
      });
      if (cancelled) return;
      setDiscordConnected(linked.linked);
      setDiscordUsername(linked.username);
      if (!discordSupported) return;
      const { connected, username } = await DiscordPresenceService.getStatus();
      if (cancelled) return;
      if (connected) {
        setDiscordConnected(true);
        setDiscordUsername(username || linked.username);
      }
      // The link lives on the server, so a previously linked account has to be
      // brought back up here rather than read from the device.
      if (!connected) {
        const restored = await DiscordPresenceService.ensureConnected(backendService);
        if (cancelled || !restored.ok) return;
        setDiscordConnected(true);
        setDiscordUsername(restored.username);
      }
    })();
    // Connecting is asynchronous, so the first read can land before it lands.
    const unsubscribe = DiscordPresenceService.addStatusListener(({ connected, username }) => {
      setDiscordConnected(connected);
      setDiscordUsername(username);
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [discordSupported, backendService]);

  const handleDiscordConnect = async () => {
    setDiscordBusy(true);
    setDiscordMessage('');
    try {
      if (!discordSupported) {
        const popup = window.open('', 'discord-link', 'width=520,height=720');
        if (!popup) throw new Error('Allow pop-ups to link your Discord account.');
        const { authorization_url, callback_origin } = await backendService.startDiscordWebLink();
        popup.location.assign(authorization_url);
        const result = await new Promise((resolve, reject) => {
          const timeout = window.setTimeout(() => finish(new Error('Discord linking timed out.')), 10 * 60 * 1000);
          const finish = (error, value) => {
            window.clearTimeout(timeout);
            window.removeEventListener('message', onMessage);
            error ? reject(error) : resolve(value);
          };
          const onMessage = (event) => {
            if (event.source !== popup || event.origin !== callback_origin || event.data?.type !== 'discord-link-result') return;
            if (!event.data.ok) finish(new Error(event.data.error || 'Discord linking failed.'));
            else finish(null, event.data);
          };
          window.addEventListener('message', onMessage);
        });
        setDiscordConnected(true);
        setDiscordUsername(result.username || null);
        setDiscordSettings(prev => ({ ...prev, enabled: true }));
        return;
      }
      const { ok, username, reason } = await DiscordPresenceService.link(backendService);
      setDiscordConnected(ok);
      setDiscordUsername(username);
      // Linking is an explicit action, so treat it as opting in.
      if (ok) setDiscordSettings(prev => ({ ...prev, enabled: true }));
      else setDiscordMessage(reason || 'Could not link Discord.');
    } finally {
      setDiscordBusy(false);
    }
  };

  const handleDiscordDisconnect = async () => {
    setDiscordBusy(true);
    try {
      await DiscordPresenceService.unlink(backendService);
      setDiscordConnected(false);
      setDiscordUsername(null);
      setDiscordSettings(prev => ({ ...prev, enabled: false }));
    } finally {
      setDiscordBusy(false);
    }
  };

  const handleDiscordSettingChange = (key, value) => {
    setDiscordSettings(prev => ({ ...prev, [key]: value }));
    // Turning the master switch off should take effect immediately rather than
    // waiting for the user to save and finish their current workout.
    if (key === 'enabled' && !value) {
      DiscordPresenceService.clearActivity();
    }
  };

  const handleHcConnect = async () => {
    setHcMessage('');
    const { ok, granted, rawGranted } = await HealthConnectService.requestPermissions();
    setHcGrantedTypes(granted || []);
    setHcRawGranted(rawGranted || []);
    setHcPermissionGranted(ok);
    backendService.updateHealthConnectStatus({
      available: hcAvailable,
      status: hcStatus,
      granted_types: granted || [],
      last_sync_at: hcLastSyncAt
    }).catch((error) => console.warn('Could not save Health Connect status:', error));
    if (!granted || granted.length === 0) {
      setHcMessage(
        'Android did not show the permission picker (this happens after Health Connect ' +
        'has been asked before). Tap "Open Health Connect Settings" and toggle on ' +
        'Weight and Sleep under Regimen.'
      );
      return;
    }
    if (!ok) {
      const missing = HealthConnectService.READ_TYPES.filter((t) => !granted.includes(t));
      setHcMessage(`Granted: ${granted.join(', ')}. Missing: ${missing.join(', ')}. Syncing what we can…`);
    }
    await handleHcSyncNow();
  };

  const handleHcOpenSettings = async () => {
    setHcMessage('');
    const res = await HealthConnectService.openSettings();
    if (!res.ok) {
      setHcMessage(`Could not open Health Connect settings${res.reason ? `: ${res.reason}` : ''}.`);
    } else {
      setHcMessage('After granting permissions in Health Connect, come back and tap "Re-check Permissions".');
    }
  };

  const handleHcRecheck = async () => {
    setHcMessage('');
    const status = await HealthConnectService.getPermissionStatus();
    setHcGrantedTypes(status.grantedTypes);
    setHcRawGranted(status.rawGranted);
    setHcPermissionGranted(status.hasAll);
    backendService.updateHealthConnectStatus({
      available: hcAvailable,
      status: hcStatus,
      granted_types: status.grantedTypes,
      last_sync_at: hcLastSyncAt
    }).catch((error) => console.warn('Could not save Health Connect status:', error));
    if (status.rawGranted.length === 0) {
      setHcMessage('Still no permissions granted. Open Health Connect settings and toggle them on.');
    } else if (!status.hasAll) {
      const missing = HealthConnectService.READ_TYPES.filter((t) => !status.grantedTypes.includes(t));
      setHcMessage(`Granted: ${status.grantedTypes.join(', ') || '(none mapped)'}. Missing: ${missing.join(', ')}. Raw: ${status.rawGranted.join(' | ')}`);
    } else {
      setHcMessage('All permissions granted.');
    }
  };

  const handleHcSyncNow = async () => {
    if (hcSyncing) return;
    setHcSyncing(true);
    setHcMessage('');
    try {
      const res = await HealthConnectService.syncRecent(backendService);
      if (!res?.ok) {
        setHcMessage(res?.reason === 'no-permission'
          ? 'Health Connect permissions are required.'
          : `Sync failed${res?.reason ? `: ${res.reason}` : ''}.`);
      } else {
        const processed = res.processed ?? 0;
        setHcMessage(processed > 0
          ? `Synced ${processed} day${processed === 1 ? '' : 's'} from Health Connect.`
          : 'Already up to date.');
        const last = await HealthConnectService.getLastSyncAt();
        setHcLastSyncAt(last);
        backendService.updateHealthConnectStatus({
          available: hcAvailable,
          status: hcStatus,
          granted_types: hcGrantedTypes,
          last_sync_at: last
        }).catch((error) => console.warn('Could not save Health Connect status:', error));
      }
    } catch (err) {
      setHcMessage(err?.message || 'Sync failed.');
    } finally {
      setHcSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // If it's already a simple YYYY-MM-DD string, return it as is
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }
    const date = new Date(dateString);
    // Use UTC methods to prevent timezone shifts
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const openAvatarSelector = () => {
    const scope = avatarSelectionScope.current;
    const selectionIdentity = authenticatedIdentity;
    const isSelectionActive = () => scope?.active && backendService.authenticatedIdentity === selectionIdentity;
    open(AvatarSelectionModal, {
      backendService,
      clerkAvatarUrl,
      isUsingCustomAvatar,
      onSelect: (file, url) => {
        if (!isSelectionActive()) {
          if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
          return;
        }
        console.log('[avatar] onSelect from modal', { name: file?.name, size: file?.size, type: file?.type });
        if (!(file instanceof Blob) || !file.size || !file.type.startsWith('image/')) {
          if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
          alert('Please choose a nonempty image file.');
          return;
        }
        // The selector transfers a preview URL; the cropper owns its own source URL.
        if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
        open(ImageCropperModal, {
          file,
          onCrop: (croppedFile, croppedUrl) => {
            if (!isSelectionActive()) {
              if (croppedUrl?.startsWith('blob:')) URL.revokeObjectURL(croppedUrl);
              return;
            }
            avatarFileRef.current = croppedFile;
            setAvatarFile(croppedFile);
            setAvatarUrl(croppedUrl);
          }
        });
      },
      onUseAccountPicture: () => handleUseAccountPicture(isSelectionActive)
    });
  };

  /**
   * Reset avatar to use the picture from the user's sign-in account
   */
  const handleUseAccountPicture = async (isSelectionActive) => {
    if (!isSelectionActive()) return;
    try {
      const result = await backendService.useAccountPicture();
      if (!isSelectionActive()) return;
      if (result?.avatar_url) {
        setAvatarUrl(result.avatar_url);
        setAvatarFile(null); // Clear any pending file upload
      }
    } catch (error) {
      if (!isSelectionActive()) return;
      console.error('Error using account picture:', error);
      alert(error.message || 'Failed to use account picture');
    }
  };

  /**
   * Check if the current avatar is different from the account picture
   */
  const isUsingCustomAvatar = avatarUrl && clerkAvatarUrl && avatarUrl !== clerkAvatarUrl;

  const updateLedger = entries => { ledgerRef.current = entries; setSaveLedger(entries); };
  const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  // Audience arrays and replacement model maps are complete values, not PATCH subsets.
  // Profile/notification/Discord GETs may include metadata outside the submitted fields.
  const matchesSaved = (actual, expected, complete = false) => {
    const unverifiable = () => { throw new Error('Saved-state response incomplete or malformed; outcome remains uncertain.'); };
    if (Array.isArray(expected)) {
      if (!Array.isArray(actual) || !actual.every(value => ['public', 'friends', 'dojo', 'dojo_chief'].includes(value)) || new Set(actual).size !== actual.length) return unverifiable();
      return actual.length === expected.length && expected.every(value => actual.includes(value));
    }
    if (expected && typeof expected === 'object') {
      if (!actual || typeof actual !== 'object' || Array.isArray(actual)) return unverifiable();
      const keys = Object.keys(expected);
      if (complete && !Object.values(actual).every(value => typeof value === 'string' && value.length)) return unverifiable();
      // Validate every requested field before treating any difference as a known mismatch.
      const matches = keys.map(key => {
        if (!Object.prototype.hasOwnProperty.call(actual, key)) return unverifiable();
        return matchesSaved(actual[key], expected[key], key === 'overrides');
      });
      return (!(complete || !keys.length) || Object.keys(actual).length === keys.length) && matches.every(Boolean);
    }
    if (actual === undefined || (actual !== null && typeof actual === 'object') || (expected !== null && typeof actual !== typeof expected)) return unverifiable();
    return actual === expected;
  };
  const refreshSavedPreferences = async () => {
    if (savingPreferences.current || scopeInvalidRef.current) return;
    savingPreferences.current = true; setPreferencesSaving(true);
    const failures = [];
    try {
      try { await onCoachChange?.(); } catch (_) { failures.push('Coach callback'); }
      if (backendService.refreshAuthentication) {
        try { await backendService.refreshAuthentication(); } catch (_) { failures.push('authentication'); }
        try { const updated = await backendService.fetchUserProfile(); if (!scopeInvalidRef.current) setAvatarUrl(updated?.avatar_url || null); } catch (_) { failures.push('profile'); }
      }
      if (scopeInvalidRef.current) return;
      if (failures.length) { setRefreshNeeded(true); setSaveMessage(`Saved; couldn't refresh (${failures.join(', ')}). Refresh does not repeat saved writes.`); }
      else { setRefreshNeeded(false); setSaveMessage('Saved settings refreshed.'); }
    } finally { savingPreferences.current = false; setPreferencesSaving(false); }
  };
  const reconcilePreferences = async () => {
    if (savingPreferences.current || scopeInvalidRef.current) return;
    savingPreferences.current = true; setPreferencesSaving(true);
    try {
      const entries = [...ledgerRef.current];
      for (let i = 0; i < entries.length; i++) {
        const item = entries[i];
        if (item.status !== 'uncertain') continue;
        const reads = {
          models: async () => (await backendService.fetchLlmModels()).settings,
          profile: () => backendService.fetchUserProfile(),
          approvals: async () => (await backendService.fetchUserProfile()).ai_coach_settings?.auto_acceptance,
          notifications: async () => (await backendService.fetchUserProfile()).notification_preferences,
          discord: () => backendService.getDiscordPresenceSettings(),
          privacy: () => backendService.getPrivacySettings(),
        };
        const actual = await reads[item.id]();
        if (scopeInvalidRef.current) return;
        if (!actual || typeof actual !== 'object') throw new Error('Saved-state response unavailable; outcome remains uncertain.');
        entries[i] = { ...item, status: matchesSaved(actual, item.payload) ? 'saved' : 'failed' };
      }
      updateLedger(entries); setSaveMessage('Saved state checked. Review remaining steps before retrying.');
    } catch (error) { setSaveMessage(error.message || 'Could not check saved state. Outcome remains uncertain.'); }
    finally { savingPreferences.current = false; setPreferencesSaving(false); }
  };
  const savePreferences = async () => {
    if (savingPreferences.current || scopeInvalidRef.current || refreshNeeded || ledgerRef.current.some(item => item.status === 'uncertain')) return;
    if (profileOwner?.service !== backendService || profileOwner.identity !== backendService.authenticatedIdentity) { setSaveMessage('Your account scope changed. Close and reopen before saving.'); return; }
    savingPreferences.current = true; setPreferencesSaving(true); setSaveMessage('');
    const identity = backendService.authenticatedIdentity;
    const isCurrent = () => !scopeInvalidRef.current && backendService.authenticatedIdentity === identity;
    try {
      // A failed ownership read is never permission for a personal-write fallback.
      if (coachOwnerType) {
        const current = await backendService.getMyDojo();
        if (!isCurrent()) return;
        if ((current ? 'dojo' : 'personal') !== coachOwnerType || (current && (current._id !== dojoSettings?.id || current.user_membership?.role !== dojoSettings?.role))) {
          scopeInvalidRef.current = true; setScopeInvalid(true); throw new Error('Coach owner or role changed. Drafts are quarantined; close and reopen to review.');
        }
        if (current) {
          const rule = await backendService.getDojoRequirements(current._id);
          if (!isCurrent()) return;
          setDojoRequiredSettings(rule?.requirements || null); setRequirementsKnown(Boolean(rule?.requirements));
          if (!rule?.requirements) throw new Error('Requirements unknown. Refresh ownership before saving Coach choices.');
          for (const [key, required] of Object.entries(rule.requirements.coachAutoAcceptance || {})) {
            if (required && !coachAutoAcceptance[key] && coachAutoAcceptance[key] !== savedCoachAutoAcceptance[key]) throw new Error(`Required Coach setting changed: ${key}. Review the refreshed rule; your draft is retained.`);
          }
        }
      }
      const profile = { phone_number: phoneNumber || null, location_tracking: locationTracking, location_capture_enabled: locationCaptureEnabled };
      if (coachOwnerType === 'personal') profile.ai_coach_settings = {
        ...(hasAiCoachFeature ? { name: aiCoachName, emoji: aiCoachEmoji, image_url: aiCoachImageUrl, coach_personality: { preset: aiCoachPersonalityPreset, custom: aiCoachPersonalityPreset === 'custom' ? aiCoachPersonalityCustom : '' }, intensity: aiCoachIntensity, intensity_custom: aiCoachIntensity === 'custom' ? aiCoachIntensityCustom : '' } : {}),
        auto_acceptance: coachAutoAcceptance,
      };
      const changed = Object.fromEntries(Object.entries(coachAutoAcceptance).filter(([key, value]) => value !== savedCoachAutoAcceptance[key]));
      const privacy = Object.fromEntries(Object.entries(privacySettings).filter(([key]) => privacyDirtyRows[key]));
      const steps = [
        ...(coachOwnerType === 'personal' && hasAiCoachFeature ? [{ id: 'models', label: 'Models', payload: { preset: llmDraft.preset, overrides: llmDraft.overrides }, write: payload => backendService.updateLlmSettings(payload) }] : []),
        { id: 'profile', label: 'Profile', payload: profile, write: payload => backendService.updateUserProfile(payload) },
        ...(coachOwnerType === 'dojo' && Object.keys(changed).length ? [{ id: 'approvals', label: 'Coach changes', payload: changed, write: payload => backendService.updateCoachAutoAcceptance(payload) }] : []),
        { id: 'notifications', dirty: sectionDirty('notifications'), label: 'Notifications', payload: notificationPrefs, write: payload => backendService.updateNotificationPreferences(payload) },
        { id: 'discord', label: 'Discord', payload: discordSettings, write: payload => backendService.updateDiscordPresenceSettings(payload), continueOnFailure: true },
        { id: 'privacy', dirty: sectionDirty('privacy'), label: 'Privacy', payload: privacy, write: payload => backendService.updatePrivacySettings(payload) },
      ];
      const entries = steps.map(step => ({ id: step.id, label: step.label, payload: step.payload, status: ledgerRef.current.some(item => item.id === step.id && item.status === 'saved' && sameValue(item.payload, step.payload)) ? 'saved' : 'not attempted' }));
      updateLedger([...entries]);
      for (let i = 0; i < steps.length; i++) {
        if (!isCurrent()) return;
        if (entries[i].status === 'saved') continue;
        entries[i].status = 'uncertain'; updateLedger([...entries]);
        try { await steps[i].write(steps[i].payload); entries[i].status = 'saved'; }
        catch (error) {
          entries[i].status = error.settingsWriteOutcome || ((error.request && !error.response) || ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error.code) ? 'uncertain' : 'failed');
          setSaveMessage(error?.response?.data?.error || error.message || 'Could not save settings.');
          updateLedger([...entries]);
          if (!steps[i].continueOnFailure) return;
        }
        updateLedger([...entries]);
      }
      if (!isCurrent() || entries.some(item => item.status !== 'saved')) return;
      setSavedCoachAutoAcceptance(coachAutoAcceptance); setPrivacyDirtyRows({}); setBroadBaseline(broadDraft);
      setLocationTrackingEnabled(locationTracking);
      const failures = [];
      try { await onCoachChange?.(); } catch (_) { failures.push('Coach callback'); }
      if (backendService.refreshAuthentication) {
        try { await backendService.refreshAuthentication(); } catch (_) { failures.push('authentication'); }
        try { const updated = await backendService.fetchUserProfile(); if (isCurrent()) setAvatarUrl(updated?.avatar_url || null); } catch (_) { failures.push('profile'); }
      }
      if (!isCurrent()) return;
      if (failures.length) { setRefreshNeeded(true); setSaveMessage(`Saved; couldn't refresh (${failures.join(', ')}). Refresh does not repeat saved writes.`); }
      else close();
    } catch (error) { setSaveMessage(error?.response?.data?.error || error.message || 'Could not save settings. Refresh ownership to review; drafts are retained.'); }
    finally { savingPreferences.current = false; setPreferencesSaving(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  if (isPreferencesMode) return savePreferences();
    try {
      const fileToUpload = avatarFileRef.current || avatarFile;
      console.log('[avatar] submit clicked', { hasAvatarFile: !!avatarFile, fileName: avatarFile?.name, fileSize: avatarFile?.size, fileType: avatarFile?.type });
      // if avatarFile present, upload first
      if (fileToUpload && backendService.uploadProfilePicture) {
        const form = new FormData();
        form.append('avatar', fileToUpload);
        console.log('[avatar] uploading avatar to backend', { name: fileToUpload.name, size: fileToUpload.size, type: fileToUpload.type });
        const uploadResult = await backendService.uploadProfilePicture(form);
        if (uploadResult?.avatar_url) {
          console.log('[avatar] upload success', uploadResult.avatar_url);
          setAvatarUrl(uploadResult.avatar_url);
          // The file is now persisted server-side. Clear it so a later save
          // does not re-upload the same image and create an orphaned object.
          setAvatarFile(null);
          avatarFileRef.current = null;
        } else {
          // Previously the save continued here and reported overall success,
          // so the user was told the profile saved while the avatar silently
          // did not change. Stop and tell them instead.
          throw new Error('Your photo could not be saved. Please try uploading it again.');
        }
      }


      let finalHeight = height;
      if (heightUnit === 'ft') {
          // Convert to cm
          const ft = parseFloat(heightFt) || 0;
          const inc = parseFloat(heightIn) || 0;
          finalHeight = Math.round(((ft * 12) + inc) * 2.54 * 10) / 10;
      } else {
        finalHeight = height ? Number(height) : null;
      }

      if (!dojoSettings) {
        await backendService.updateLlmSettings({ preset: llmDraft.preset, overrides: llmDraft.overrides });
      }

      await backendService.updateUserProfile({
        email,
        display_name: display_name,
        date_of_birth: date_of_birth,
        timezone: timezone,
        height: finalHeight,
        sex,
        dietary_restrictions: dietaryRestrictions,
        medical_issues: medicalIssues,
        phone_number: phoneNumber || null,
        location_tracking: locationTracking,
        location_capture_enabled: locationCaptureEnabled,
        progress_portrait_public: progressPortraitPublic,
        ...(!dojoSettings && { ai_coach_settings: {
          name: aiCoachName,
          emoji: aiCoachEmoji,
          image_url: aiCoachImageUrl,
          coach_personality: {
            preset: aiCoachPersonalityPreset,
            custom: aiCoachPersonalityPreset === 'custom' ? aiCoachPersonalityCustom : ''
          },
          intensity: aiCoachIntensity,
          intensity_custom: aiCoachIntensity === 'custom' ? aiCoachIntensityCustom : '',

          auto_acceptance: coachAutoAcceptance
        } })
      });

      if (dojoSettings) {
        const changed = Object.fromEntries(Object.entries(coachAutoAcceptance).filter(([key, value]) => value !== savedCoachAutoAcceptance[key]));
        if (Object.keys(changed).length) {
          await backendService.updateCoachAutoAcceptance(changed);
          setSavedCoachAutoAcceptance(previous => ({ ...previous, ...changed }));
        }
      }

      // Save notification preferences
      await backendService.updateNotificationPreferences(notificationPrefs);

      // Save Discord presence settings
      try {
        await backendService.updateDiscordPresenceSettings(discordSettings);
      } catch (e) {
        console.warn('Could not save Discord presence settings:', e);
      }

      // Sync location tracking to GeolocationServiceProvider
      setLocationTrackingEnabled(locationTracking);

      // Loaded audiences may already be stale; only explicit row edits are writes.
      const privacyUpdates = Object.fromEntries(
        Object.entries(privacySettings).filter(([key]) => privacyDirtyRows[key])
      );
      await backendService.updatePrivacySettings(privacyUpdates);
      setPrivacyDirtyRows({});

      // Notify the app after the server-backed coach settings mutation succeeds.
      onCoachChange?.();

      // Refresh cached auth state so avatar propagates
      if (backendService.refreshAuthentication) {
        await backendService.refreshAuthentication();
        // Re-fetch user to refresh modal preview
        const updated = await backendService.fetchUserProfile();
        setAvatarUrl(updated?.avatar_url || null);
      }
      close();
    } catch (error) {
      alert(`Failed to update profile: ${error?.response?.data?.error || error?.message || 'Unknown error'}`);
    }
  };



  // Email verification handlers
  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    setVerificationMessage('');
    try {
      const result = await backendService.resendVerificationEmail();
      setVerificationMessage(result.message || 'Verification email sent!');
    } catch (error) {
      setVerificationMessage(error?.response?.data?.error || 'Failed to send verification email');
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) return;
    try {
      const result = await backendService.verifyEmailWithCode(verificationCode.trim());
      if (result.success) {
        setEmailVerified(true);
        setVerificationMessage('Email verified successfully! 🎉');
        setVerificationCode('');
      }
    } catch (error) {
      setVerificationMessage(error?.response?.data?.error || 'Invalid verification code');
    }
  };

  const handleNotificationPrefChange = (notificationType, channel) => {
    setNotificationPrefs(prev => ({
      ...prev,
      [notificationType]: {
        ...prev[notificationType],
        [channel]: !prev[notificationType]?.[channel]
      }
    }));
  };

  const requestBrowserNotificationPermission = async () => {
    if (typeof Notification === 'undefined') {
      alert('Browser notifications are not supported in this browser');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setBrowserNotificationPermission(permission);
      if (permission === 'granted') new Notification('Notifications Enabled! 🔔', {
        body: 'You will now receive browser notifications from Kata.fit.', icon: '/images/icon-192.png'
      });
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  const requestNativeNotificationPermission = async () => {
    const permission = await NativePushNotificationService.enableFromUserGesture(backendService);
    setNativeNotificationPermission(permission);
  };

  const openAndroidNotificationSettings = () => NativePushNotificationService.openSettings();

  /**
   * Toggle a privacy audience for an activity type.
   * Handles special logic for 'public' and 'private' (clears others when selected).
   */
  const handlePrivacyAudienceToggle = (activityType, audience) => {
    const required = activityType === 'progress_image' ? null : dojoRequiredSettings?.privacy?.[activityType];
    const current = privacySettings[activityType] || [];
    if (required && (current.includes(required) || current.includes('public')) &&
      (audience === 'private' || (audience === 'public' && current.includes('public')) || audience === required)) return;
    setPrivacyDirtyRows(prev => ({ ...prev, [activityType]: true }));
    setPrivacySettings(prev => {
      const currentAudiences = Array.isArray(prev[activityType]) ? prev[activityType] : [];

      // Special handling for 'private' - clears all others
      if (audience === 'private') {
        // If already private (empty array), do nothing
        // Otherwise, set to empty array (private)
        return { ...prev, [activityType]: [] };
      }

      // Special handling for 'public' - clears all others
      if (audience === 'public') {
        // Toggle public - if adding public, clear others (public implies all)
        if (currentAudiences.includes('public')) {
          return { ...prev, [activityType]: [] };
        } else {
          return { ...prev, [activityType]: ['public'] };
        }
      }

      // If currently public and toggling another option, switch to just that option
      if (currentAudiences.includes('public')) {
        return { ...prev, [activityType]: [audience] };
      }

      // Toggle the audience
      if (currentAudiences.includes(audience)) {
        return { ...prev, [activityType]: currentAudiences.filter(a => a !== audience) };
      } else {
        return { ...prev, [activityType]: [...currentAudiences, audience] };
      }
    });
  };

  const handleLogout = async () => {
    if (!onLogout || logoutState.pending) return;
    setLogoutState({ pending: true, error: '' });
    try {
      await onLogout();
    } catch (error) {
      setLogoutState({ pending: false, error: error?.message || 'Could not log out. Please try again.' });
    }
  };

    const notificationsContent = <>
          {/* Notifications Section */}
          <div className="preferences-section notifications-section" ref={notificationsSectionRef}>
            <h3 className="section-title">🔔 Notifications</h3>

            {/* Browser Notification Permission */}
            <div className="browser-notification-setup">
              <div className="notification-permission-status">
                <span className="permission-label">{nativePush ? 'Android Notifications:' : 'Browser Notifications:'}</span>
                {notificationPermission === 'granted' ? (
                  <span className="permission-granted">✅ Enabled</span>
                ) : nativePush && notificationPermission === 'denied' ? (
                  <button type="button" className="btn-enable-notifications" onClick={openAndroidNotificationSettings}>Open Android notification settings</button>
                ) : !nativePush && notificationPermission === 'denied' ? (
                  <span className="permission-denied">❌ Blocked (enable in browser settings)</span>
                ) : (
                  <button
                    type="button"
                    className="btn-enable-notifications"
                    onClick={nativePush ? requestNativeNotificationPermission : requestBrowserNotificationPermission}
                  >
                    {nativePush ? 'Enable Android Notifications' : 'Enable Browser Notifications'}
                  </button>
                )}
              </div>
            </div>

            {/* Phone Number for SMS */}
            <div className="form-group sms-setup">
              <label className="user-profile-label">📱 Phone Number (for SMS notifications)</label>
              <input
                type="tel"
                className="user-profile-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 555-123-4567"
              />
              <p className="field-hint">Optional. Standard SMS rates may apply.</p>
            </div>

            {/* Notification Preferences Grid */}
            <div className="notification-prefs-grid">
              <p className="prefs-description">Choose how you'd like to receive each type of notification:</p>

              <div className="notification-grid-header">
                <span className="notification-type-header">Notification Type</span>
                <span className="channel-header">📧</span>
                <span className="channel-header">📱</span>
                <span className="channel-header">🔔</span>
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Messages from friends</span>
                <input type="checkbox" checked={notificationPrefs.messages?.email} onChange={() => handleNotificationPrefChange('messages', 'email')} />
                <input type="checkbox" checked={notificationPrefs.messages?.sms} onChange={() => handleNotificationPrefChange('messages', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.messages?.push} onChange={() => handleNotificationPrefChange('messages', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Dojo messages</span>
                <input type="checkbox" checked={notificationPrefs.dojo_messages?.email} onChange={() => handleNotificationPrefChange('dojo_messages', 'email')} />
                <input type="checkbox" checked={notificationPrefs.dojo_messages?.sms} onChange={() => handleNotificationPrefChange('dojo_messages', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.dojo_messages?.push} onChange={() => handleNotificationPrefChange('dojo_messages', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Dojo peer activity completions</span>
                <input type="checkbox" checked={notificationPrefs.dojo_activity_completions?.email} onChange={() => handleNotificationPrefChange('dojo_activity_completions', 'email')} />
                <input type="checkbox" checked={notificationPrefs.dojo_activity_completions?.sms} onChange={() => handleNotificationPrefChange('dojo_activity_completions', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.dojo_activity_completions?.push} onChange={() => handleNotificationPrefChange('dojo_activity_completions', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Coach messages and feedback</span>
                <input type="checkbox" checked={notificationPrefs.coach_messages?.email} onChange={() => handleNotificationPrefChange('coach_messages', 'email')} />
                <input type="checkbox" checked={notificationPrefs.coach_messages?.sms} onChange={() => handleNotificationPrefChange('coach_messages', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.coach_messages?.push} onChange={() => handleNotificationPrefChange('coach_messages', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Friend requests</span>
                <input type="checkbox" checked={notificationPrefs.friend_requests?.email} onChange={() => handleNotificationPrefChange('friend_requests', 'email')} />
                <input type="checkbox" checked={notificationPrefs.friend_requests?.sms} onChange={() => handleNotificationPrefChange('friend_requests', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.friend_requests?.push} onChange={() => handleNotificationPrefChange('friend_requests', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Dojo invitations</span>
                <input type="checkbox" checked={notificationPrefs.dojo_invites?.email} onChange={() => handleNotificationPrefChange('dojo_invites', 'email')} />
                <input type="checkbox" checked={notificationPrefs.dojo_invites?.sms} onChange={() => handleNotificationPrefChange('dojo_invites', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.dojo_invites?.push} onChange={() => handleNotificationPrefChange('dojo_invites', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Dojo join requests</span>
                <input type="checkbox" checked={notificationPrefs.dojo_join_requests?.email} onChange={() => handleNotificationPrefChange('dojo_join_requests', 'email')} />
                <input type="checkbox" checked={notificationPrefs.dojo_join_requests?.sms} onChange={() => handleNotificationPrefChange('dojo_join_requests', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.dojo_join_requests?.push} onChange={() => handleNotificationPrefChange('dojo_join_requests', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Comments on your activities</span>
                <input type="checkbox" checked={notificationPrefs.activity_comments?.email} onChange={() => handleNotificationPrefChange('activity_comments', 'email')} />
                <input type="checkbox" checked={notificationPrefs.activity_comments?.sms} onChange={() => handleNotificationPrefChange('activity_comments', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.activity_comments?.push} onChange={() => handleNotificationPrefChange('activity_comments', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Kudos on your activities</span>
                <input type="checkbox" checked={notificationPrefs.activity_kudos?.email} onChange={() => handleNotificationPrefChange('activity_kudos', 'email')} />
                <input type="checkbox" checked={notificationPrefs.activity_kudos?.sms} onChange={() => handleNotificationPrefChange('activity_kudos', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.activity_kudos?.push} onChange={() => handleNotificationPrefChange('activity_kudos', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Scheduled activities</span>
                <input type="checkbox" checked={notificationPrefs.scheduled_activities?.email} onChange={() => handleNotificationPrefChange('scheduled_activities', 'email')} />
                <input type="checkbox" checked={notificationPrefs.scheduled_activities?.sms} onChange={() => handleNotificationPrefChange('scheduled_activities', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.scheduled_activities?.push} onChange={() => handleNotificationPrefChange('scheduled_activities', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Weekly progress summary</span>
                <input type="checkbox" checked={notificationPrefs.weekly_summary?.email} onChange={() => handleNotificationPrefChange('weekly_summary', 'email')} />
                <input type="checkbox" checked={notificationPrefs.weekly_summary?.sms} onChange={() => handleNotificationPrefChange('weekly_summary', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.weekly_summary?.push} onChange={() => handleNotificationPrefChange('weekly_summary', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>
            </div>
          </div>
  </>;
  const healthDiscordContent = <>
          {/* Health Connect is read-only away from the Android device that owns it. */}
          {
            <div className="preferences-section health-connect-section">
              <h3 className="section-title">❤️ Health Connect</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary, #aaa)', marginTop: '4px' }}>
                Import weight and sleep from Health Connect on your Android device.
              </p>
              {!hcNative && (
                <p style={{ fontSize: '13px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                  These are the last settings reported by your Android device. To change permissions or sync, open Kata.fit on that device.
                </p>
              )}
              {!hcAvailable && (hcNative || hcStatus) && (
                <p style={{ fontSize: '13px', color: '#e07a5f', marginTop: '8px' }}>
                  {hcStatus === 'NotInstalled'
                    ? 'Install the Health Connect app from the Play Store to enable syncing.'
                    : hcStatus === 'NotSupported'
                      ? 'Health Connect is not supported on this device.'
                      : 'Health Connect is unavailable.'}
                </p>
              )}
              {hcAvailable && hcNative && (
                <>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                    {!hcPermissionGranted ? (
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handleHcConnect}
                        disabled={hcSyncing}
                      >
                        {hcGrantedTypes.length === 0 ? 'Connect Health Connect' : 'Grant Missing Permissions'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handleHcSyncNow}
                        disabled={hcSyncing}
                      >
                        {hcSyncing ? 'Syncing…' : 'Sync Now'}
                      </button>
                    )}
                    {hcGrantedTypes.length > 0 && !hcPermissionGranted && (
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handleHcSyncNow}
                        disabled={hcSyncing}
                      >
                        {hcSyncing ? 'Syncing…' : 'Sync Now'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleHcOpenSettings}
                      disabled={hcSyncing}
                    >
                      Open Health Connect Settings
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleHcRecheck}
                      disabled={hcSyncing}
                    >
                      Re-check Permissions
                    </button>
                  </div>
                  {hcGrantedTypes.length > 0 && (
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                      Granted: {hcGrantedTypes.join(', ')}
                      {!hcPermissionGranted && (
                        <> · Missing: {HealthConnectService.READ_TYPES.filter((t) => !hcGrantedTypes.includes(t)).join(', ')}</>
                      )}
                    </p>
                  )}
                  {hcRawGranted.length > 0 && (
                    <p style={{ fontSize: '11px', color: 'var(--text-secondary, #777)', marginTop: '4px', wordBreak: 'break-all' }}>
                      Raw HC perms: {hcRawGranted.join(' | ')}
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                    Last sync: {hcLastSyncAt ? new Date(hcLastSyncAt).toLocaleString() : 'never'}
                  </p>
                </>
              )}
              {!hcNative && (
                <>
                  {!hcReportedAt && (
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                      No Health Connect status has been reported yet. Open Kata.fit on your Android device once to refresh it here.
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                    Permissions: {hcGrantedTypes.length > 0 ? hcGrantedTypes.join(', ') : 'none reported'}
                    {!hcPermissionGranted && hcGrantedTypes.length > 0 && (
                      <> · Missing: {HealthConnectService.READ_TYPES.filter((t) => !hcGrantedTypes.includes(t)).join(', ')}</>
                    )}
                  </p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                    Last sync: {hcLastSyncAt ? new Date(hcLastSyncAt).toLocaleString() : 'never'}
                  </p>
                </>
              )}
              {hcMessage && (
                <p style={{ fontSize: '13px', color: '#7fb069', marginTop: '4px' }}>{hcMessage}</p>
              )}
            </div>
          }

          {/* Discord linking and preferences are account settings; publishing remains native-only. */}
          {
            <div className="preferences-section discord-presence-section">
              <h3 className="section-title">🎮 Discord</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary, #aaa)', marginTop: '4px' }}>
                {discordSupported
                  ? 'Show your live workout on your Discord profile while you train. Presence clears automatically when you finish.'
                  : 'Link your Discord account and manage your Rich Presence preferences. Live workout presence is published from the mobile app.'}
              </p>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px', alignItems: 'center' }}>
                {discordConnected ? (
                  <>
                    <span style={{ fontSize: '13px', color: '#7fb069' }}>
                      Linked{discordUsername ? ` as ${discordUsername}` : ''}
                    </span>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={handleDiscordDisconnect}
                      disabled={discordBusy}
                    >
                      {discordBusy ? 'Working…' : 'Unlink'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="primary-button"
                    onClick={handleDiscordConnect}
                    disabled={discordBusy}
                  >
                    {discordBusy ? 'Connecting…' : 'Link Discord account'}
                  </button>
                )}
              </div>

              {discordConnected && (
                <div style={{ marginTop: '12px' }}>
                  {[
                    ['enabled', 'Share workout activity', 'Master switch for Discord presence.'],
                    ['show_workout_name', 'Show workout name', 'e.g. "Push Day A"'],
                    ['show_exercise', 'Show current exercise', 'e.g. "Bench Press"'],
                    ['show_sets', 'Show set progress', 'e.g. "Set 3 of 5"']
                  ].map(([key, label, hint]) => (
                    <div className="location-tracking-setting" key={key}>
                      <div className="location-tracking-info">
                        <span className="location-tracking-label">{label}</span>
                        <span className="location-tracking-desc">{hint}</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={!!discordSettings[key]}
                          disabled={key !== 'enabled' && !discordSettings.enabled}
                          onChange={(e) => handleDiscordSettingChange(key, e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {discordMessage && (
                <p style={{ fontSize: '13px', color: '#7fb069', marginTop: '8px' }}>{discordMessage}</p>
              )}
            </div>
          }
  </>;
  const mcpContent = <>
          {/* Custom MCP Servers Section */}
          <div className="preferences-section">
            <CustomMcpSettings
              backendService={backendService}
              scope="personal"
            />
          </div>
  </>;


  // Reconcile only membership rules after the nested dedicated Save. Never reload
  // the profile or replace the caller's unsaved personal choices.
  const reconcileMembershipRules = async () => {
    const identity = backendService.authenticatedIdentity;
    const dojoId = dojoSettings?.id;
    if (!dojoId || scopeInvalidRef.current) return;
    setRequirementsKnown(false);
    try {
      const rule = await backendService.getDojoRequirements(dojoId);
      if (scopeInvalidRef.current || backendService.authenticatedIdentity !== identity) return;
      setDojoRequiredSettings(rule?.requirements || null);
      setRequirementsKnown(Boolean(rule?.requirements));
    } catch (_) {
      if (!scopeInvalidRef.current && backendService.authenticatedIdentity === identity) {
        setDojoRequiredSettings(null); setRequirementsKnown(false);
      }
    }
  };

  const broadDraft = React.useMemo(() => ({
    coach: { name: aiCoachName, emoji: aiCoachEmoji, image: aiCoachImageUrl, intensity: aiCoachIntensity, intensityCustom: aiCoachIntensityCustom, personality: aiCoachPersonalityPreset, personalityCustom: aiCoachPersonalityCustom, auto: coachAutoAcceptance, llm: { preset: llmDraft.preset, overrides: llmDraft.overrides } },
    notifications: { phoneNumber, notificationPrefs },
    privacy: { locationTracking, locationCaptureEnabled, privacySettings },
    connections: { discordSettings },
  }), [aiCoachName, aiCoachEmoji, aiCoachImageUrl, aiCoachIntensity, aiCoachIntensityCustom, aiCoachPersonalityPreset, aiCoachPersonalityCustom, coachAutoAcceptance, llmDraft, phoneNumber, notificationPrefs, locationTracking, locationCaptureEnabled, privacySettings, discordSettings]);
  useEffect(() => { if (!isLoading && !broadBaseline) setBroadBaseline(broadDraft); }, [isLoading, broadBaseline, broadDraft]);
  const sectionDirty = id => Boolean(broadBaseline && JSON.stringify(broadBaseline[id]) !== JSON.stringify(broadDraft[id]));
  const broadDirty = broadBaseline && Object.keys(broadDraft).some(sectionDirty);
  const closePreferences = React.useCallback(() => {
    if (savingPreferences.current) return false;
    return !broadDirty || window.confirm('Discard unsaved settings drafts? Independently saved actions will not be undone.');
  }, [broadDirty]);
  useEffect(() => isPreferencesMode ? modalService.onBeforeClose?.(closePreferences) : undefined, [isPreferencesMode, modalService, closePreferences]);

  if (isLoading) {
    return (
      <div className={`user-profile-modal loading ${isPreferencesMode ? 'preferences-mode' : 'profile-mode'}`}>
        <ModalLoading text="Loading your profile..." />
      </div>
    );
  }

  if (isPreferencesMode) return <div className="user-profile-modal preferences-mode coherent-preferences">
    <form id="preferences-settings-form" onSubmit={handleSubmit}>
      <SettingsShell title="Preferences" context={dojoSettings ? `${dojoSettings.name} · Personal choices` : 'Personal experience & consent'} initialSection={targetSection}
        sections={[
          { id: 'coach', label: 'Coach', summary: 'Behavior, models & automatic changes', dirty: sectionDirty('coach'), content: <fieldset className="preferences-owner-fence" disabled={scopeInvalid || preferencesSaving}><p className="settings-authority">{dojoSettings ? `Set by ${dojoSettings.name}` : coachOwnerType === 'personal' ? 'Yours' : 'Coach ownership unknown'}</p>
{dojoSettings ? <div className="settings-inherited-summary"><strong>{dojoSettings.coach.name || 'Dojo Coach'}</strong><p>{dojoSettings.coach.intensity || 'Dojo-defined behavior'} · Models: {dojoSettings.models.preset || 'Dojo-defined'}</p><button type="button" className="settings-backlink" onClick={() => open(DojoSettingsModal, { backendService, scrollToSection: 'coach' })}>View Dojo Coach settings</button></div>
 : coachOwnerType !== 'personal' ? <p role="alert">Coach ownership could not be confirmed. No personal model or persona edits are available. Refresh ownership to review.</p>
 : !hasAiCoachFeature ? <div className="settings-locked-persona"><p>Personal appearance and personality require Pro. Your consent choices below remain available.</p><button type="button" onClick={openUpgrade}>Upgrade to Pro →</button></div>
 : <><AiCoachSettings name={aiCoachName} setName={setAiCoachName} emoji={aiCoachEmoji} imageUrl={aiCoachImageUrl} setEmoji={setAiCoachEmoji} setImageUrl={setAiCoachImageUrl} setImageFile={setAiCoachImageFile} intensity={aiCoachIntensity} intensityCustom={aiCoachIntensityCustom} setIntensity={setAiCoachIntensity} setIntensityCustom={setAiCoachIntensityCustom} personalityPreset={aiCoachPersonalityPreset} personalityCustom={aiCoachPersonalityCustom} setPersonalityPreset={setAiCoachPersonalityPreset} setPersonalityCustom={setAiCoachPersonalityCustom} />          {/* AI Models Section */}
          <div className="preferences-section">
            <AiModelSettings
              backendService={backendService}
              readOnly={coachOwnerType !== 'personal'}
              establishedBy={dojoSettings?.name}
              onViewEstablished={() => open(DojoSettingsModal, { backendService })}
              onSettingsChange={captureLlmDraft}
            />
          </div>

</>}
            <div className="location-tracking-setting coach-auto-acceptance-setting">
              <div className="location-tracking-info">
                <span className="location-tracking-label">Coach changes</span>
                <span className="location-tracking-desc">Supported automatic changes may take effect without asking you first. Turning this off stops future automatic changes; it does not undo changes already applied.</span>
              </div>
            </div>
            {[
              ['daily_insight_changes', 'Daily insight changes', 'Personal nutrition or progress-photo requirement changes from daily coaching.'],
              ['exercise_coach_changes', 'Exercise coach changes', 'Personal set, rep, weight, or exercise adjustments proposed during a workout.']
            ].map(([key, label, description]) => (
              <div className="location-tracking-setting" key={key}>
                <div className="location-tracking-info">
                  <label className="location-tracking-label" htmlFor={`coach-consent-${key}`}>{label}</label>
                  <span className="location-tracking-desc">{description}</span><span className="settings-authority">{dojoRequiredSettings?.coachAutoAcceptance?.[key] ? `Required by ${dojoSettings?.name}` : 'Yours'}</span><span>{coachAutoAcceptance[key] ? 'Apply automatically' : 'Ask me first'}</span>
                  {dojoRequiredSettings?.coachAutoAcceptance?.[key] === true && <span>Required by {dojoSettings?.name}; {dojoSettings?.role === 'chief' ? 'change the saved membership rule before disabling; transferring leadership does not remove the rule' : 'leave before disabling'}.</span>}
                </div>
                <label className={`toggle-switch ${dojoRequiredSettings?.coachAutoAcceptance?.[key] === true && !!coachAutoAcceptance[key] ? 'required-locked' : ''}`}>
                  <input id={`coach-consent-${key}`} type="checkbox" checked={!!coachAutoAcceptance[key]} disabled={!coachOwnerType || (coachOwnerType === 'dojo' && !requirementsKnown) || (dojoRequiredSettings?.coachAutoAcceptance?.[key] === true && !!coachAutoAcceptance[key])} onChange={(e) => setCoachAutoAcceptance(prev => ({ ...prev, [key]: e.target.checked }))} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            ))}
            {coachOwnerType === 'dojo' && !requirementsKnown && <p role="alert">Requirements unknown. Refresh ownership before changing required choices.</p>}
            {dojoSettings && <button type="button" className="settings-backlink" onClick={() => open(DojoSettingsModal, { backendService, scrollToSection: 'members', onRequirementsUpdated: reconcileMembershipRules })}>View membership rule</button>}
            <p className="settings-note">External Coach requires automatic changes to be off. Daily insight automatic changes always conflict.</p><details className="settings-conflict-detail"><summary>Dojo workout exception</summary><p>Only Dojo-owned numeric workout and exercise suggestions permit the exercise automatic-change flag. This exception never applies to daily insights, other task kinds, or personal standalone Coach selection.</p></details>
</fieldset> },
          { id: 'notifications', dirty: sectionDirty('notifications'), label: 'Notifications', summary: 'Device readiness & delivery', content: <fieldset className="preferences-owner-fence" disabled={scopeInvalid || preferencesSaving}><p className="settings-note">Saved preferences do not verify device delivery.</p>{notificationsContent}

</fieldset> },
          { id: 'privacy', dirty: sectionDirty('privacy'), label: 'Privacy & capture', summary: 'Sharing, location & camera', content: <fieldset className="preferences-owner-fence" disabled={scopeInvalid || preferencesSaving}>          {/* Privacy Settings Section */}
          <div className="preferences-section privacy-section">
            <h3 className="section-title">🔐 Privacy Settings</h3>
            {(!coachOwnerType || (coachOwnerType === 'dojo' && !requirementsKnown)) && <p role="alert">Requirements unknown. Capture and raw activity audiences are locked until ownership and membership rules are confirmed. Independent progress sharing remains yours.</p>}

            <div className="location-tracking-setting">
              <div className="location-tracking-info">
                <span className="location-tracking-label">📍 Location Tracking</span>
                <span className="location-tracking-desc">
                  Allow Kata.fit to use your device's location to automatically detect which gym you're at during workouts. Location is only used while the app is open.
                </span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={locationTracking}
                  onChange={(e) => setLocationTracking(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <CameraSettingsSection backendService={backendService} identity={backendService.authenticatedIdentity} ownerId={backendService.positionOwner} />



            <div className="location-tracking-setting">
              <div className="location-tracking-info">
                <span className="location-tracking-label">Capture position when filing an activity and for app events</span>
                <span className="location-tracking-desc">Off by default. Enabling requests device permission with a fresh location check; no coordinates are saved by this check. While the app is in the foreground and permission is granted, fresh cached GPS coordinates can be saved with activity changes and app lifecycle events in your private activity log. This is not background tracking. Disabling stops future capture but does not delete already recorded location history. Position sharing and gym tracking are separate settings. Filing an activity with capture enabled still needs fresh GPS. No saved gym or manual pin is used.</span>
              </div>
              <label className={`toggle-switch ${dojoRequiredSettings?.location_capture_enabled === true && locationCaptureEnabled ? 'required-locked' : ''}`}>
                <input type="checkbox" checked={locationCaptureEnabled} disabled={!coachOwnerType || (coachOwnerType === 'dojo' && !requirementsKnown) || (dojoRequiredSettings?.location_capture_enabled === true && locationCaptureEnabled)} onChange={e => {
                  setLocationCaptureEnabled(e.target.checked);
                  if (e.target.checked) captureDevice.request();
                  else captureDevice.reset();
                }} />
                <span className="toggle-slider"></span>
              </label>
            </div>
            {locationCaptureEnabled && <div>
              <button type="button" disabled={captureDevice.checking} onClick={captureDevice.request}>{captureDevice.checking ? 'Checking device location…' : captureDevice.status ? 'Retry device location' : 'Check / request device location'}</button>
              {captureDevice.status && <p role="status">{deviceLocationMessages[captureDevice.status]}</p>}
              <p>Device permission is separate from your Capture position preference. Save settings to keep your preference. A failed check does not tell us whether system Location Services are off.</p>
            </div>}
            {dojoRequiredSettings?.location_capture_enabled === true && <p>Required by {dojoSettings?.name}; {dojoSettings?.role === 'chief' ? 'change the saved membership rule before disabling; transferring leadership does not remove the rule' : 'leave before disabling'}.</p>}
            <BackgroundPositionSettings backendService={backendService} />
            <p className="prefs-description">Choose who can see each type of activity:</p>
            <p role="alert" className="privacy-hint"><strong>Warning:</strong> Public Position sharing exposes your exact coordinates to anyone. Friends, Dojo and Dojo Chief also receive exact coordinates when selected. Private is the default.</p>

            <div className="privacy-settings-grid">
              {ACTIVITY_TYPES.map(({ key, label, description }) => {
                const audiences = Array.isArray(privacySettings[key]) ? privacySettings[key] : [];
                const isPrivate = audiences.length === 0;
                const required = key === 'progress_image' ? null : dojoRequiredSettings?.privacy?.[key];
                const compliant = Boolean(required && (audiences.includes(required) || audiences.includes('public')));

                return (
                  <div key={key} className="privacy-setting-row">
                    <div className="privacy-activity-info">
                      <span className="privacy-activity-label">{label}</span>
                      <span className="privacy-activity-desc">{description}</span>
                    </div>
                    {key === 'progress_image' && !progressPrivacyLoaded && <span role="status">Progress image sharing could not be loaded. Reopen settings to retry; saving will not change it unless you explicitly choose an audience.</span>}
                    {required && <span>Required by {dojoSettings?.name}: {required === 'dojo_chief' ? 'Dojo Chief' : required}; {dojoSettings?.role === 'chief' ? 'change the saved membership rule before disabling; transferring leadership does not remove the rule' : 'leave before disabling'}.</span>}
                    <div className="privacy-audiences">
                      {PRIVACY_AUDIENCES.map(opt => (
                        <label
                          key={opt.value}
                          className={`privacy-audience-chip ${opt.value === 'private' ? (isPrivate ? 'selected' : '') : audiences.includes(opt.value) ? 'selected' : ''} ${compliant && (opt.value === 'private' || (opt.value === 'public' && audiences.includes('public')) || (opt.value === required && audiences.includes(required))) ? 'required-locked' : ''}`}
                          title={opt.description}
                        >
                          <input
                            type="checkbox"
                            checked={opt.value === 'private' ? isPrivate : audiences.includes(opt.value)}
                            disabled={((!coachOwnerType || (coachOwnerType === 'dojo' && !requirementsKnown)) && key !== 'progress_image') || (compliant && (opt.value === 'private' || (opt.value === 'public' && audiences.includes('public')) || (opt.value === required && audiences.includes(required))))}
                            onChange={() => handlePrivacyAudienceToggle(key, opt.value)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="privacy-hint">
              💡 <strong>Private</strong> = only you. <strong>Public</strong> = everyone.
            </p>
          </div>

<ExternalCoachDataGrant backendService={backendService} ownerType={coachOwnerType} authorityKey={dojoSettings?.id || 'personal'} /></fieldset> },
          { id: 'connections', dirty: sectionDirty('connections'), label: 'Connections', summary: 'Tools, health & linked accounts', content: <fieldset className="preferences-owner-fence" disabled={scopeInvalid || preferencesSaving}><p className="settings-note">Personal MCP tools are available to your Coach and your Dojo’s authorized Coach while you are a member. Calls may have tool-specific side effects without per-call approval. Removing a connection stops future access, not completed effects. Optional read grants and plan-change consent are separate.</p><button type="button" className="settings-backlink" onClick={() => setTargetSection({ section: 'coach' })}>Model providers → Coach</button>
{dojoSettings ? <p>Shared external Coach setup is managed by your Dojo. <button type="button" className="settings-backlink" onClick={() => open(DojoSettingsModal, { backendService, scrollToSection: 'connections' })}>View Dojo connections</button></p> : coachOwnerType === 'personal' && <ExternalCoachAgentSettings backendService={backendService} />}
{healthDiscordContent}

{mcpContent}

</fieldset> },
        ]}
        footerClassName="preferences-settings-actions" footer={<>
          {saveMessage && <p role="alert">{saveMessage}</p>}
          {saveLedger.length > 0 && <p role="status">{saveLedger.map(item => `${item.label}: ${item.status}`).join(' · ')}</p>}
          {broadDirty && !saveLedger.length && <p role="status">Unsaved changes</p>}
          {refreshNeeded && <button type="button" disabled={preferencesSaving || scopeInvalid} onClick={refreshSavedPreferences}>Refresh saved settings</button>}
          {saveLedger.some(item => item.status === 'uncertain') && <button type="button" disabled={preferencesSaving || scopeInvalid} onClick={reconcilePreferences}>Check saved state</button>}
          <button type="button" className="btn-cancel" disabled={preferencesSaving} onClick={() => { if (closePreferences()) close(); }}>Close</button>
          <button type="submit" className="btn-save" disabled={preferencesSaving || scopeInvalid || refreshNeeded || saveLedger.some(item => item.status === 'uncertain')}>{preferencesSaving ? 'Saving…' : 'Save changes'}</button>
        </>}
      />
    </form>
  </div>;

  return (
    <div className={`user-profile-modal ${isPreferencesMode ? 'preferences-mode' : 'profile-mode'}`}>
      <div className="user-profile-header">
        <h2>{isPreferencesMode ? 'User Preferences' : 'Profile & Account'}</h2>
      </div>

      <div className="user-profile-body">
        {!isPreferencesMode && profileOwner?.service === backendService && profileOwner.identity === authenticatedIdentity && profileOwner.id && <ProgressImagePanel backendService={backendService} userId={profileOwner.id} />}
        {!isPreferencesMode && <div className="avatar-section profile-details-section progress-portrait-section">
          <button
            type="button"
            className="profile-progress-open"
            onClick={onOpenCompliance}
            disabled={!onOpenCompliance}
            aria-label="View progress and compliance details"
          >
            <ProgressPortrait
              user={{ display_name, email, avatar_url: avatarUrl }}
              progressPercent={progressPercent}
              monthPercent={monthPercent}
              yearPercent={yearPercent}
              starCount={starCount}
              progressState={progressState || (progressUnavailable ? 'unavailable' : progressPercent === null || progressPercent === undefined ? 'loading' : 'ready')}
              size="profile"
            />
          </button>
          <div className="profile-progress-copy">
            <strong>Progress portrait</strong>
            <span>{progressUnavailable ? 'Progress unavailable' : progressState === 'loading' || (!progressState && (progressPercent === null || progressPercent === undefined)) ? 'Progress is still loading' : progressPercent === null || progressPercent === undefined ? 'Progress unavailable' : `${Math.round(progressPercent)}% 7-day plan compliance`}</span>
          </div>
          <div className="profile-portrait-actions">
            <button type="button" className="profile-portrait-change" onClick={openAvatarSelector}>Change photo</button>
            <button type="button" className="profile-portrait-details" onClick={onOpenCompliance} disabled={!onOpenCompliance}>View progress details</button>
          </div>
          <label className="profile-portrait-visibility">
            <input
              type="checkbox"
              checked={progressPortraitPublic}
              onChange={event => setProgressPortraitPublic(event.target.checked)}
            />
            <span>
              <strong>Show my progress decoration</strong>
              <small>Share the halo with friends and Dojo members. Detailed nutrition and workout percentages stay inside authorized profile views.</small>
            </span>
          </label>
        </div>}

        <form className="user-profile-form" onSubmit={handleSubmit}>
          <div className="profile-details-section">
          <div className="form-group">
            <label className="user-profile-label" htmlFor="display_name">Display Name</label>
            <input
              className="user-profile-input"
              type="text"
              id="display_name"
              value={display_name}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="user-profile-label" htmlFor="email">Email</label>
            <input
              className="user-profile-input"
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {/* Email Verification Status - inline with email field */}
            <div className="email-verification-inline">
              {emailVerified ? (
                <span className="verified-badge">✅ Verified</span>
              ) : (
                <div className="unverified-section">
                  <span className="unverified-badge">⚠️ Not verified</span>
                  <div className="verification-inline-actions">
                    <input
                      type="text"
                      placeholder="Code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="verification-code-input-small"
                      maxLength={6}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyCode}
                      className="btn-verify-small"
                      disabled={!verificationCode.trim()}
                    >
                      ✓
                    </button>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      className="btn-resend-small"
                      disabled={isResendingVerification}
                    >
                      {isResendingVerification ? '...' : 'Resend'}
                    </button>
                  </div>
                  {verificationMessage && (
                    <p className="verification-message-small">{verificationMessage}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="user-profile-label" htmlFor="date_of_birth">Date of Birth</label>
            <input
              className="user-profile-input"
              type="date"
              id="date_of_birth"
              value={date_of_birth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label className="user-profile-label" htmlFor="height" style={{ marginBottom: 0 }}>Height</label>
                <div style={{ display: 'flex', gap: '5px', fontSize: '0.8rem' }}>
                    <button
                        type="button"
                        onClick={() => setHeightUnit('cm')}
                        className={heightUnit === 'cm' ? 'unit-btn active' : 'unit-btn'}
                        style={{
                            background: heightUnit === 'cm' ? '#4f46e5' : '#333',
                            border: 'none',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        cm
                    </button>
                    <button
                        type="button"
                        onClick={() => setHeightUnit('ft')}
                        className={heightUnit === 'ft' ? 'unit-btn active' : 'unit-btn'}
                        style={{
                            background: heightUnit === 'ft' ? '#4f46e5' : '#333',
                            border: 'none',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        ft/in
                    </button>
                </div>
            </div>
            {heightUnit === 'cm' ? (
                <input
                  className="user-profile-input"
                  type="number"
                  id="height"
                  value={height}
                  onChange={(e) => {
                      setHeight(e.target.value);
                      // Update derived ft/in
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val)) {
                          const inches = val / 2.54;
                          setHeightFt(Math.floor(inches / 12));
                          setHeightIn(Math.round(inches % 12));
                      }
                  }}
                  placeholder="e.g. 175"
                />
            ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 1 }}>
                        <input
                            className="user-profile-input"
                            type="number"
                            value={heightFt}
                            onChange={(e) => {
                                setHeightFt(e.target.value);
                                // Update derived cm
                                const ft = parseFloat(e.target.value) || 0;
                                const inc = parseFloat(heightIn) || 0;
                                setHeight(Math.round(((ft * 12) + inc) * 2.54 * 10) / 10);
                            }}
                            placeholder="ft"
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <input
                            className="user-profile-input"
                            type="number"
                            value={heightIn}
                            onChange={(e) => {
                                setHeightIn(e.target.value);
                                // Update derived cm
                                const ft = parseFloat(heightFt) || 0;
                                const inc = parseFloat(e.target.value) || 0;
                                setHeight(Math.round(((ft * 12) + inc) * 2.54 * 10) / 10);
                            }}
                            placeholder="in"
                        />
                    </div>
                </div>
            )}
          </div>

          <div className="form-group">
            <label className="user-profile-label" htmlFor="sex">Sex</label>
            <select
              className="user-profile-input"
              id="sex"
              value={sex}
              onChange={(e) => setSex(e.target.value)}
            >
              <option value="">Not Specified</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>

          <div className="form-group">
            <label className="user-profile-label" htmlFor="timezone">Timezone</label>
            <select
              className="user-profile-input"
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {supportedTimezones.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="user-profile-label" htmlFor="dietary_restrictions">Dietary Restrictions & Preferences</label>
            <textarea
              className="user-profile-input"
              id="dietary_restrictions"
              value={dietaryRestrictions}
              onChange={(e) => setDietaryRestrictions(e.target.value)}
              placeholder="E.g., Vegan, Gluten-free, No peanuts..."
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label className="user-profile-label" htmlFor="medical_issues">Medical Issues & Injuries</label>
            <textarea
              className="user-profile-input"
              id="medical_issues"
              value={medicalIssues}
              onChange={(e) => setMedicalIssues(e.target.value)}
              placeholder="E.g., Bad knees, Lower back pain..."
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>

          </div>

          {/* AI Assistant Settings Section */}
          <div ref={aiCoachSectionRef} className={`preferences-section ai-coach-section ${!hasAiCoachFeature ? 'locked' : ''}`}>
            <h3 className="ai-coach-section-title">
              🤖 AI Assistant Settings
              {!hasAiCoachFeature && <span className="pro-badge-inline">PRO</span>}
            </h3>

            {!hasAiCoachFeature ? (
              <div className="ai-coach-locked-overlay" onClick={openUpgrade}>
                <div className="ai-coach-locked-content">
                  <span className="lock-icon">🔒</span>
                  <p>Customize your AI assistant experience</p>
                  <ul className="locked-features-list">
                    <li>Choose your assistant's appearance and personality</li>
                    <li>Set your preferred communication style and tone</li>
                    <li>Adjust push intensity to match your motivation needs</li>
                  </ul>
                  <button className="btn-upgrade-inline">Upgrade to Pro →</button>
                </div>
              </div>
            ) : (
            <>
            {dojoSettings ? (
              <AiCoachSettings
                name={aiCoachName}
                setName={setAiCoachName}
                emoji={aiCoachEmoji}
                imageUrl={aiCoachImageUrl}
                setEmoji={setAiCoachEmoji}
                setImageUrl={setAiCoachImageUrl}
                setImageFile={setAiCoachImageFile}

                intensity={aiCoachIntensity}
                intensityCustom={aiCoachIntensityCustom}
                setIntensity={setAiCoachIntensity}
                setIntensityCustom={setAiCoachIntensityCustom}
                personalityPreset={aiCoachPersonalityPreset}
                personalityCustom={aiCoachPersonalityCustom}
                setPersonalityPreset={setAiCoachPersonalityPreset}
                setPersonalityCustom={setAiCoachPersonalityCustom}
                readOnly
                establishedBy={dojoSettings.name}
                onViewEstablished={() => open(DojoSettingsModal, { backendService })}
              />
            ) : (
            <>
            <AiCoachSettings
              name={aiCoachName}
              setName={setAiCoachName}
              emoji={aiCoachEmoji}
              imageUrl={aiCoachImageUrl}
              setEmoji={setAiCoachEmoji}
              setImageUrl={setAiCoachImageUrl}
              setImageFile={setAiCoachImageFile}

              intensity={aiCoachIntensity}
              intensityCustom={aiCoachIntensityCustom}
              setIntensity={setAiCoachIntensity}
              setIntensityCustom={setAiCoachIntensityCustom}
              personalityPreset={aiCoachPersonalityPreset}
              personalityCustom={aiCoachPersonalityCustom}
              setPersonalityPreset={setAiCoachPersonalityPreset}
              setPersonalityCustom={setAiCoachPersonalityCustom}
            />
            <div style={{ display: 'none' }}>
            <div className="form-group">
              <label className="user-profile-label">Assistant Appearance</label>
              <div className="ai-coach-appearance">
                <div className="ai-coach-preview">
                  {aiCoachImageUrl ? (
                    <img src={aiCoachImageUrl} alt="AI Assistant" className="ai-coach-image" />
                  ) : (
                    <span className="ai-coach-emoji-preview">{aiCoachEmoji}</span>
                  )}
                </div>
                <div className="ai-coach-emoji-grid">
                  {AI_EMOJI_OPTIONS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      className={`ai-coach-emoji-btn ${aiCoachEmoji === emoji && !aiCoachImageUrl ? 'selected' : ''}`}
                      onClick={() => { setAiCoachEmoji(emoji); setAiCoachImageUrl(null); }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <div className="ai-coach-image-upload">
                  <input
                    type="file"
                    id="ai-coach-image"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const url = URL.createObjectURL(file);
                        setAiCoachImageFile(file);
                        setAiCoachImageUrl(url);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-select"
                    onClick={() => document.getElementById('ai-coach-image').click()}
                  >
                    📷 Use Custom Image
                  </button>
                  {aiCoachImageUrl && (
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => { setAiCoachImageUrl(null); setAiCoachImageFile(null); }}
                      style={{ marginLeft: '8px' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="user-profile-label">Push Intensity</label>
              <div className="ai-coach-intensity-slider">
                {AI_INTENSITY_LEVELS.map(level => (
                  <button
                    key={level.id}
                    type="button"
                    className={`ai-coach-intensity-btn ${aiCoachIntensity === level.id ? 'selected' : ''}`}
                    onClick={() => setAiCoachIntensity(level.id)}
                  >
                    <span className="intensity-label">{level.label}</span>
                  </button>
                ))}
              </div>
              <p className="intensity-description">
                {AI_INTENSITY_LEVELS.find(l => l.id === aiCoachIntensity)?.description}
              </p>
              {aiCoachIntensity === 'custom' && (
                <textarea
                  className="user-profile-input"
                  value={aiCoachIntensityCustom}
                  onChange={(e) => setAiCoachIntensityCustom(e.target.value)}
                  placeholder="Describe your preferred intensity level (e.g., 'Check in every 2 hours', 'Only remind me when I miss a goal')..."
                  rows={2}
                  style={{ resize: 'vertical', marginTop: '12px' }}
                />
              )}
            </div>
            </div>
            </>
            )}
            {dojoSettings
              ? <p className="ai-settings-established-notice">External Coach access is managed in dojo settings. <button type="button" className="ai-settings-link" onClick={() => open(DojoSettingsModal, { backendService })}>View dojo settings</button></p>
              : coachOwnerType === 'personal' && <ExternalCoachAgentSettings backendService={backendService} />}
            </>
            )}
          </div>

          {/* Privacy controls stay accessible even without paid settings access. */}
          <ExternalCoachDataGrant backendService={backendService} ownerType={coachOwnerType} authorityKey={dojoSettings?.id || 'personal'} />

          {/* Notifications Section */}
          <div className="preferences-section notifications-section" ref={notificationsSectionRef}>
            <h3 className="section-title">🔔 Notifications</h3>

            {/* Browser Notification Permission */}
            <div className="browser-notification-setup">
              <div className="notification-permission-status">
                <span className="permission-label">{nativePush ? 'Android Notifications:' : 'Browser Notifications:'}</span>
                {notificationPermission === 'granted' ? (
                  <span className="permission-granted">✅ Enabled</span>
                ) : nativePush && notificationPermission === 'denied' ? (
                  <button type="button" className="btn-enable-notifications" onClick={openAndroidNotificationSettings}>Open Android notification settings</button>
                ) : !nativePush && notificationPermission === 'denied' ? (
                  <span className="permission-denied">❌ Blocked (enable in browser settings)</span>
                ) : (
                  <button
                    type="button"
                    className="btn-enable-notifications"
                    onClick={nativePush ? requestNativeNotificationPermission : requestBrowserNotificationPermission}
                  >
                    {nativePush ? 'Enable Android Notifications' : 'Enable Browser Notifications'}
                  </button>
                )}
              </div>
            </div>

            {/* Phone Number for SMS */}
            <div className="form-group sms-setup">
              <label className="user-profile-label">📱 Phone Number (for SMS notifications)</label>
              <input
                type="tel"
                className="user-profile-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 555-123-4567"
              />
              <p className="field-hint">Optional. Standard SMS rates may apply.</p>
            </div>

            {/* Notification Preferences Grid */}
            <div className="notification-prefs-grid">
              <p className="prefs-description">Choose how you'd like to receive each type of notification:</p>

              <div className="notification-grid-header">
                <span className="notification-type-header">Notification Type</span>
                <span className="channel-header">📧</span>
                <span className="channel-header">📱</span>
                <span className="channel-header">🔔</span>
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Messages from friends</span>
                <input type="checkbox" checked={notificationPrefs.messages?.email} onChange={() => handleNotificationPrefChange('messages', 'email')} />
                <input type="checkbox" checked={notificationPrefs.messages?.sms} onChange={() => handleNotificationPrefChange('messages', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.messages?.push} onChange={() => handleNotificationPrefChange('messages', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Dojo messages</span>
                <input type="checkbox" checked={notificationPrefs.dojo_messages?.email} onChange={() => handleNotificationPrefChange('dojo_messages', 'email')} />
                <input type="checkbox" checked={notificationPrefs.dojo_messages?.sms} onChange={() => handleNotificationPrefChange('dojo_messages', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.dojo_messages?.push} onChange={() => handleNotificationPrefChange('dojo_messages', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Dojo peer activity completions</span>
                <input type="checkbox" checked={notificationPrefs.dojo_activity_completions?.email} onChange={() => handleNotificationPrefChange('dojo_activity_completions', 'email')} />
                <input type="checkbox" checked={notificationPrefs.dojo_activity_completions?.sms} onChange={() => handleNotificationPrefChange('dojo_activity_completions', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.dojo_activity_completions?.push} onChange={() => handleNotificationPrefChange('dojo_activity_completions', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Coach messages and feedback</span>
                <input type="checkbox" checked={notificationPrefs.coach_messages?.email} onChange={() => handleNotificationPrefChange('coach_messages', 'email')} />
                <input type="checkbox" checked={notificationPrefs.coach_messages?.sms} onChange={() => handleNotificationPrefChange('coach_messages', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.coach_messages?.push} onChange={() => handleNotificationPrefChange('coach_messages', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Friend requests</span>
                <input type="checkbox" checked={notificationPrefs.friend_requests?.email} onChange={() => handleNotificationPrefChange('friend_requests', 'email')} />
                <input type="checkbox" checked={notificationPrefs.friend_requests?.sms} onChange={() => handleNotificationPrefChange('friend_requests', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.friend_requests?.push} onChange={() => handleNotificationPrefChange('friend_requests', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Dojo invitations</span>
                <input type="checkbox" checked={notificationPrefs.dojo_invites?.email} onChange={() => handleNotificationPrefChange('dojo_invites', 'email')} />
                <input type="checkbox" checked={notificationPrefs.dojo_invites?.sms} onChange={() => handleNotificationPrefChange('dojo_invites', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.dojo_invites?.push} onChange={() => handleNotificationPrefChange('dojo_invites', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Dojo join requests</span>
                <input type="checkbox" checked={notificationPrefs.dojo_join_requests?.email} onChange={() => handleNotificationPrefChange('dojo_join_requests', 'email')} />
                <input type="checkbox" checked={notificationPrefs.dojo_join_requests?.sms} onChange={() => handleNotificationPrefChange('dojo_join_requests', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.dojo_join_requests?.push} onChange={() => handleNotificationPrefChange('dojo_join_requests', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Comments on your activities</span>
                <input type="checkbox" checked={notificationPrefs.activity_comments?.email} onChange={() => handleNotificationPrefChange('activity_comments', 'email')} />
                <input type="checkbox" checked={notificationPrefs.activity_comments?.sms} onChange={() => handleNotificationPrefChange('activity_comments', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.activity_comments?.push} onChange={() => handleNotificationPrefChange('activity_comments', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Kudos on your activities</span>
                <input type="checkbox" checked={notificationPrefs.activity_kudos?.email} onChange={() => handleNotificationPrefChange('activity_kudos', 'email')} />
                <input type="checkbox" checked={notificationPrefs.activity_kudos?.sms} onChange={() => handleNotificationPrefChange('activity_kudos', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.activity_kudos?.push} onChange={() => handleNotificationPrefChange('activity_kudos', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Scheduled activities</span>
                <input type="checkbox" checked={notificationPrefs.scheduled_activities?.email} onChange={() => handleNotificationPrefChange('scheduled_activities', 'email')} />
                <input type="checkbox" checked={notificationPrefs.scheduled_activities?.sms} onChange={() => handleNotificationPrefChange('scheduled_activities', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.scheduled_activities?.push} onChange={() => handleNotificationPrefChange('scheduled_activities', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>

              <div className="notification-row">
                <span className="notification-type-label">Weekly progress summary</span>
                <input type="checkbox" checked={notificationPrefs.weekly_summary?.email} onChange={() => handleNotificationPrefChange('weekly_summary', 'email')} />
                <input type="checkbox" checked={notificationPrefs.weekly_summary?.sms} onChange={() => handleNotificationPrefChange('weekly_summary', 'sms')} disabled={!phoneNumber} />
                <input type="checkbox" checked={notificationPrefs.weekly_summary?.push} onChange={() => handleNotificationPrefChange('weekly_summary', 'push')} disabled={notificationPermission !== 'granted'} />
              </div>
            </div>
          </div>

          {/* Health Connect is read-only away from the Android device that owns it. */}
          {
            <div className="preferences-section health-connect-section">
              <h3 className="section-title">❤️ Health Connect</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary, #aaa)', marginTop: '4px' }}>
                Import weight and sleep from Health Connect on your Android device.
              </p>
              {!hcNative && (
                <p style={{ fontSize: '13px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                  These are the last settings reported by your Android device. To change permissions or sync, open Kata.fit on that device.
                </p>
              )}
              {!hcAvailable && (hcNative || hcStatus) && (
                <p style={{ fontSize: '13px', color: '#e07a5f', marginTop: '8px' }}>
                  {hcStatus === 'NotInstalled'
                    ? 'Install the Health Connect app from the Play Store to enable syncing.'
                    : hcStatus === 'NotSupported'
                      ? 'Health Connect is not supported on this device.'
                      : 'Health Connect is unavailable.'}
                </p>
              )}
              {hcAvailable && hcNative && (
                <>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                    {!hcPermissionGranted ? (
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handleHcConnect}
                        disabled={hcSyncing}
                      >
                        {hcGrantedTypes.length === 0 ? 'Connect Health Connect' : 'Grant Missing Permissions'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handleHcSyncNow}
                        disabled={hcSyncing}
                      >
                        {hcSyncing ? 'Syncing…' : 'Sync Now'}
                      </button>
                    )}
                    {hcGrantedTypes.length > 0 && !hcPermissionGranted && (
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handleHcSyncNow}
                        disabled={hcSyncing}
                      >
                        {hcSyncing ? 'Syncing…' : 'Sync Now'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleHcOpenSettings}
                      disabled={hcSyncing}
                    >
                      Open Health Connect Settings
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleHcRecheck}
                      disabled={hcSyncing}
                    >
                      Re-check Permissions
                    </button>
                  </div>
                  {hcGrantedTypes.length > 0 && (
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                      Granted: {hcGrantedTypes.join(', ')}
                      {!hcPermissionGranted && (
                        <> · Missing: {HealthConnectService.READ_TYPES.filter((t) => !hcGrantedTypes.includes(t)).join(', ')}</>
                      )}
                    </p>
                  )}
                  {hcRawGranted.length > 0 && (
                    <p style={{ fontSize: '11px', color: 'var(--text-secondary, #777)', marginTop: '4px', wordBreak: 'break-all' }}>
                      Raw HC perms: {hcRawGranted.join(' | ')}
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                    Last sync: {hcLastSyncAt ? new Date(hcLastSyncAt).toLocaleString() : 'never'}
                  </p>
                </>
              )}
              {!hcNative && (
                <>
                  {!hcReportedAt && (
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                      No Health Connect status has been reported yet. Open Kata.fit on your Android device once to refresh it here.
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                    Permissions: {hcGrantedTypes.length > 0 ? hcGrantedTypes.join(', ') : 'none reported'}
                    {!hcPermissionGranted && hcGrantedTypes.length > 0 && (
                      <> · Missing: {HealthConnectService.READ_TYPES.filter((t) => !hcGrantedTypes.includes(t)).join(', ')}</>
                    )}
                  </p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary, #aaa)', marginTop: '8px' }}>
                    Last sync: {hcLastSyncAt ? new Date(hcLastSyncAt).toLocaleString() : 'never'}
                  </p>
                </>
              )}
              {hcMessage && (
                <p style={{ fontSize: '13px', color: '#7fb069', marginTop: '4px' }}>{hcMessage}</p>
              )}
            </div>
          }

          {/* Discord linking and preferences are account settings; publishing remains native-only. */}
          {
            <div className="preferences-section discord-presence-section">
              <h3 className="section-title">🎮 Discord</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary, #aaa)', marginTop: '4px' }}>
                {discordSupported
                  ? 'Show your live workout on your Discord profile while you train. Presence clears automatically when you finish.'
                  : 'Link your Discord account and manage your Rich Presence preferences. Live workout presence is published from the mobile app.'}
              </p>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px', alignItems: 'center' }}>
                {discordConnected ? (
                  <>
                    <span style={{ fontSize: '13px', color: '#7fb069' }}>
                      Linked{discordUsername ? ` as ${discordUsername}` : ''}
                    </span>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={handleDiscordDisconnect}
                      disabled={discordBusy}
                    >
                      {discordBusy ? 'Working…' : 'Unlink'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="primary-button"
                    onClick={handleDiscordConnect}
                    disabled={discordBusy}
                  >
                    {discordBusy ? 'Connecting…' : 'Link Discord account'}
                  </button>
                )}
              </div>

              {discordConnected && (
                <div style={{ marginTop: '12px' }}>
                  {[
                    ['enabled', 'Share workout activity', 'Master switch for Discord presence.'],
                    ['show_workout_name', 'Show workout name', 'e.g. "Push Day A"'],
                    ['show_exercise', 'Show current exercise', 'e.g. "Bench Press"'],
                    ['show_sets', 'Show set progress', 'e.g. "Set 3 of 5"']
                  ].map(([key, label, hint]) => (
                    <div className="location-tracking-setting" key={key}>
                      <div className="location-tracking-info">
                        <span className="location-tracking-label">{label}</span>
                        <span className="location-tracking-desc">{hint}</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={!!discordSettings[key]}
                          disabled={key !== 'enabled' && !discordSettings.enabled}
                          onChange={(e) => handleDiscordSettingChange(key, e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {discordMessage && (
                <p style={{ fontSize: '13px', color: '#7fb069', marginTop: '8px' }}>{discordMessage}</p>
              )}
            </div>
          }

          {/* AI Models Section */}
          <div className="preferences-section">
            <AiModelSettings
              backendService={backendService}
              readOnly={Boolean(dojoSettings)}
              establishedBy={dojoSettings?.name}
              onViewEstablished={() => open(DojoSettingsModal, { backendService })}
              onSettingsChange={captureLlmDraft}
            />
          </div>

          {/* Custom MCP Servers Section */}
          <div className="preferences-section">
            <CustomMcpSettings
              backendService={backendService}
              scope="personal"
            />
          </div>

          {/* Privacy Settings Section */}
          <div className="preferences-section privacy-section">
            <h3 className="section-title">🔐 Privacy Settings</h3>

            <div className="location-tracking-setting">
              <div className="location-tracking-info">
                <span className="location-tracking-label">📍 Location Tracking</span>
                <span className="location-tracking-desc">
                  Allow Kata.fit to use your device's location to automatically detect which gym you're at during workouts. Location is only used while the app is open.
                </span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={locationTracking}
                  onChange={(e) => setLocationTracking(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <CameraSettingsSection backendService={backendService} identity={backendService.authenticatedIdentity} ownerId={backendService.positionOwner} />

            <div className="location-tracking-setting coach-auto-acceptance-setting">
              <div className="location-tracking-info">
                <span className="location-tracking-label">🤖 Coach change approvals</span>
                <span className="location-tracking-desc">Allow the coach to apply these personal changes automatically. Dojo requirements may require these settings before you join.</span>
              </div>
            </div>
            {[
              ['daily_insight_changes', 'Daily insight changes', 'Personal nutrition or progress-photo requirement changes from daily coaching.'],
              ['exercise_coach_changes', 'Exercise coach changes', 'Personal set, rep, weight, or exercise adjustments proposed during a workout.']
            ].map(([key, label, description]) => (
              <div className="location-tracking-setting" key={key}>
                <div className="location-tracking-info">
                  <span className="location-tracking-label">{label}</span>
                  <span className="location-tracking-desc">{description}</span>
                  {dojoRequiredSettings?.coachAutoAcceptance?.[key] === true && <span>Required by {dojoSettings?.name}; {dojoSettings?.role === 'chief' ? 'change rule or transfer ownership' : 'leave before disabling'}.</span>}
                </div>
                <label className={`toggle-switch ${dojoRequiredSettings?.coachAutoAcceptance?.[key] === true && !!coachAutoAcceptance[key] ? 'required-locked' : ''}`}>
                  <input type="checkbox" checked={!!coachAutoAcceptance[key]} disabled={dojoRequiredSettings?.coachAutoAcceptance?.[key] === true && !!coachAutoAcceptance[key]} onChange={(e) => setCoachAutoAcceptance(prev => ({ ...prev, [key]: e.target.checked }))} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            ))}

            <div className="location-tracking-setting">
              <div className="location-tracking-info">
                <span className="location-tracking-label">Capture position when filing an activity and for app events</span>
                <span className="location-tracking-desc">Off by default. Enabling requests device permission with a fresh location check; no coordinates are saved by this check. While the app is in the foreground and permission is granted, fresh cached GPS coordinates can be saved with activity changes and app lifecycle events in your private activity log. This is not background tracking. Disabling stops future capture but does not delete already recorded location history. Position sharing and gym tracking are separate settings. Filing an activity with capture enabled still needs fresh GPS. No saved gym or manual pin is used.</span>
              </div>
              <label className={`toggle-switch ${dojoRequiredSettings?.location_capture_enabled === true && locationCaptureEnabled ? 'required-locked' : ''}`}>
                <input type="checkbox" checked={locationCaptureEnabled} disabled={dojoRequiredSettings?.location_capture_enabled === true && locationCaptureEnabled} onChange={e => {
                  setLocationCaptureEnabled(e.target.checked);
                  if (e.target.checked) captureDevice.request();
                  else captureDevice.reset();
                }} />
                <span className="toggle-slider"></span>
              </label>
            </div>
            {locationCaptureEnabled && <div>
              <button type="button" disabled={captureDevice.checking} onClick={captureDevice.request}>{captureDevice.checking ? 'Checking device location…' : captureDevice.status ? 'Retry device location' : 'Check / request device location'}</button>
              {captureDevice.status && <p role="status">{deviceLocationMessages[captureDevice.status]}</p>}
              <p>Device permission is separate from your Capture position preference. Save settings to keep your preference. A failed check does not tell us whether system Location Services are off.</p>
            </div>}
            {dojoRequiredSettings?.location_capture_enabled === true && <p>Required by {dojoSettings?.name}; {dojoSettings?.role === 'chief' ? 'change rule or transfer ownership' : 'leave before disabling'}.</p>}
            <BackgroundPositionSettings backendService={backendService} />
            <p className="prefs-description">Choose who can see each type of activity:</p>
            <p role="alert" className="privacy-hint"><strong>Warning:</strong> Public Position sharing exposes your exact coordinates to anyone. Friends, Dojo and Dojo Chief also receive exact coordinates when selected. Private is the default.</p>

            <div className="privacy-settings-grid">
              {ACTIVITY_TYPES.map(({ key, label, description }) => {
                const audiences = Array.isArray(privacySettings[key]) ? privacySettings[key] : [];
                const isPrivate = audiences.length === 0;
                const required = key === 'progress_image' ? null : dojoRequiredSettings?.privacy?.[key];
                const compliant = Boolean(required && (audiences.includes(required) || audiences.includes('public')));

                return (
                  <div key={key} className="privacy-setting-row">
                    <div className="privacy-activity-info">
                      <span className="privacy-activity-label">{label}</span>
                      <span className="privacy-activity-desc">{description}</span>
                    </div>
                    {key === 'progress_image' && !progressPrivacyLoaded && <span role="status">Progress image sharing could not be loaded. Reopen settings to retry; saving will not change it unless you explicitly choose an audience.</span>}
                    {required && <span>Required by {dojoSettings?.name}: {required === 'dojo_chief' ? 'Dojo Chief' : required}; {dojoSettings?.role === 'chief' ? 'change rule or transfer ownership' : 'leave before disabling'}.</span>}
                    <div className="privacy-audiences">
                      {PRIVACY_AUDIENCES.map(opt => (
                        <label
                          key={opt.value}
                          className={`privacy-audience-chip ${opt.value === 'private' ? (isPrivate ? 'selected' : '') : audiences.includes(opt.value) ? 'selected' : ''} ${compliant && (opt.value === 'private' || (opt.value === 'public' && audiences.includes('public')) || (opt.value === required && audiences.includes(required))) ? 'required-locked' : ''}`}
                          title={opt.description}
                        >
                          <input
                            type="checkbox"
                            checked={opt.value === 'private' ? isPrivate : audiences.includes(opt.value)}
                            disabled={compliant && (opt.value === 'private' || (opt.value === 'public' && audiences.includes('public')) || (opt.value === required && audiences.includes(required)))}
                            onChange={() => handlePrivacyAudienceToggle(key, opt.value)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="privacy-hint">
              💡 <strong>Private</strong> = only you. <strong>Public</strong> = everyone.
            </p>
          </div>

          <div className="button-container">
            <button type="button" className="btn-cancel" onClick={close}>Cancel</button>
            <button type="submit" className="btn-save">Save Changes</button>
          </div>
        </form>
        {!isPreferencesMode && onLogout && (
          <div className="profile-account-danger-zone">
            <button type="button" className="profile-account-logout" onClick={handleLogout} disabled={logoutState.pending}>
              {logoutState.pending ? 'Logging out…' : 'Log out'}
            </button>
            {logoutState.error && <p className="profile-account-logout-error" role="alert">{logoutState.error}</p>}
          </div>
        )}
      </div>


    </div>
  );
};

export default UserProfileModal;
